import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowUpDown,
  Bell,
  Car,
  ClipboardCheck,
  ClipboardList,
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  Fuel,
  Gauge,
  IdCard,
  LineChart as LineChartIcon,
  LogOut,
  Pencil,
  Plus,
  Save,
  Search,
  Settings,
  Shield,
  Trash2,
  Upload,
  User,
  Users,
  Wrench,
  X
} from 'lucide-react';
import AppBar from 'components/AppBar';
import FuelInvoiceTrendChart from '../components/fleet/FuelInvoiceTrendChart';
import { buildFuelInvoiceTrendData } from '../utils/fleetFuelInvoiceShared';
import {
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import * as XLSX from 'xlsx';
import {
  CATEGORY_COLORS,
  MAINTENANCE_RECORDS,
  MAINTENANCE_ITEMS,
  VIR_CHECKLIST_CATEGORIES,
  createDefaultVirChecks,
  isVirChecklistComplete,
  getVirCheckOptionLabel,
  getVirCheckOptionsForCategory,
  VIR_TICKET_ITEMS,
  createDefaultVirTickets,
  enrichVehicles,
  getCategoryBreakdown,
  DRIVING_LICENSES,
  getDrivingLicenseForVehicle,
  enrichFuelRecordsForVehicle,
  getBreakdownRecordsForVehicle,
  getBreakdownStatusLabel,
  getBreakdownStatusClass,
  getLicenseStatus,
  getNotifications,
  getUpcomingMaintenance,
  getVehicleTotalCost,
  getVirForVehicle,
  getVirResultLabel,
  getVirResultStatusClass,
  resolveVirNumber,
  buildServiceCardRows,
  getLatestInspectionOdometer,
  getServiceCardIntervalStatus,
  SERVICE_INTERVAL_KM,
  VIR_INSPECTIONS,
  BREAKDOWN_RECORDS
} from '../data/fleetMaintenanceMockData';
import {
  fetchAllCarRentals,
  readCarRentalsSessionCache,
  writeCarRentalsSessionCache
} from '../services/carRentalsService';
import {
  mergeDriverProfileWithCarRental,
  normalizeRegNumber,
  normalizePersonToken,
  buildDriverVehicleListFromCarRentals,
  resolveFleetDataVehicleId,
  driverNameMatches,
  isInactiveFleetPlate,
  isActiveFleetPlate,
  getCurrentRentalPeriod,
  driverHasCarRentalInPeriod
} from '../services/driverRentalProfileService';
import { readAuthSession, logout as authLogout } from '../services/authService';
import {
  fetchVirReportsForVehicle,
  fetchAllVirReports,
  upsertVirReport,
  getVirReportErrorMessage
} from '../services/virReportsService';
import {
  fetchDrivingLicenseForVehicle,
  fetchAllDrivingLicenses,
  upsertDrivingLicense,
  getDrivingLicenseErrorMessage
} from '../services/drivingLicensesService';
import {
  fetchServiceCardsForVehicle,
  createServiceCard,
  getServiceCardErrorMessage
} from '../services/serviceCardsService';
import {
  fetchFuelLogsForVehicle,
  createFuelLog,
  getFuelLogErrorMessage
} from '../services/fuelLogsService';
import {
  fetchAllFleetFuelInvoices,
  createFleetFuelInvoice,
  updateFleetFuelInvoice,
  deleteFleetFuelInvoice,
  calcFuelInvoiceTotal,
  getFleetFuelInvoiceErrorMessage
} from '../services/fleetFuelInvoicesService';
import {
  fetchDriverProfileForVehicle,
  upsertDriverProfile,
  getDriverProfileErrorMessage
} from '../services/driverProfilesService';
import {
  fetchAllVehicleStatuses,
  upsertVehicleStatus,
  normalizeVehicleKey
} from '../services/vehicleStatusService';
import './FleetMaintenanceCostTracking.css';

const ADMIN_NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LineChartIcon },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'vehicles', label: 'Vehicles', icon: Car },
  { id: 'maintenance', label: 'Maintenance', icon: Wrench },
  { id: 'fuel', label: 'Fuel', icon: Fuel },
  { id: 'drivers', label: 'Drivers', icon: Users },
  { id: 'driving-license', label: 'Driving License', icon: IdCard },
  { id: 'reports', label: 'Reports', icon: FileText },
  { id: 'settings', label: 'Settings', icon: Settings }
];

const DRIVER_NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', shortLabel: 'Home', icon: LineChartIcon },
  { id: 'maintenance', label: 'Maintenance', shortLabel: 'Maint.', icon: Wrench },
  { id: 'breakdown', label: 'Breakdown', shortLabel: 'Breakdown', icon: AlertTriangle },
  { id: 'fuel', label: 'Fuel', shortLabel: 'Fuel', icon: Fuel },
  { id: 'vir', label: 'Vehicle Inspection Report', shortLabel: 'VIR', icon: ClipboardCheck },
  { id: 'service-card', label: 'Service Card', shortLabel: 'Service', icon: ClipboardList },
  { id: 'driving-license', label: 'Driving License', shortLabel: 'License', icon: IdCard }
];

const PAGE_SIZE = 12;
const BRAND = '#2563EB';
const VEHICLE_LIST_META_KEY = 'fmct-vehicle-list-meta-v1';
const VEHICLE_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'due-service', label: 'Due Service' },
  { value: 'repair', label: 'In Repair' },
  { value: 'inactive', label: 'Inactive' }
];

const MAINTENANCE_REPORTS_KEY = 'fmct-maintenance-reports-v1';
const MAINTENANCE_CHANNEL_OPTIONS = ['WhatsApp', 'Email'];
const MAINTENANCE_REPORT_STATUS_OPTIONS = [
  { value: 'reported', label: 'Reported' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' }
];

const readMaintenanceReports = () => {
  try {
    const raw = localStorage.getItem(MAINTENANCE_REPORTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const readVehicleListMeta = () => {
  try {
    const raw = localStorage.getItem(VEHICLE_LIST_META_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const getVehicleListKey = (vehicle) =>
  normalizeRegNumber(vehicle?.plate) || String(vehicle?.id || '');

const formatCurrency = (n) =>
  `TZS ${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const formatTSh = (n) => {
  if (n === null || n === undefined || n === '' || Number(n) === 0) return '';
  return `TSh ${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const FUEL_INVOICE_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const FUEL_INVOICE_PAYMENT_STATUSES = [
  'Not processed',
  'Pending',
  'Processed',
  'Paid'
];

const FUEL_INVOICE_PROJECTS = ['HEP IIB (Lot 10)', 'HEP IIB (Lot 8)'];

const FUEL_INVOICE_MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

const buildFuelInvoiceYearOptions = (extraYears = []) => {
  const current = new Date().getFullYear();
  const years = new Set();
  for (let y = current + 1; y >= current - 8; y -= 1) years.add(String(y));
  (extraYears || []).forEach((y) => {
    const s = String(y || '').trim();
    if (/^\d{4}$/.test(s)) years.add(s);
  });
  return [...years].sort((a, b) => Number(b) - Number(a));
};

/** Same colored project pills as Car Rental Invoices table. */
function fuelProjectBadgeClass(project) {
  const t = String(project || '').trim();
  if (!t) return 'rental-project--empty';
  const key = t.toLowerCase().replace(/[()]/g, ' ').replace(/\s+/g, ' ').trim();
  const known = {
    admin: 'rental-project--admin',
    bts: 'rental-project--bts',
    helios: 'rental-project--helios',
    fttm: 'rental-project--fttm',
    'rea 2c': 'rental-project--rea2c',
    'hep iib lot 8': 'rental-project--hep8',
    'hep iib lot 10': 'rental-project--hep10'
  };
  if (known[key]) return known[key];
  let h = 0;
  for (let i = 0; i < t.length; i += 1) h = (h * 31 + t.charCodeAt(i)) >>> 0;
  return `rental-project--alt${h % 6}`;
}

const cellInputWidthCh = (value, minCh = 6) =>
  Math.max(minCh, String(value || '').length + 1);

/** Payment status pill tone for fuel invoices. */
function fuelPaymentStatusClass(status) {
  const s = String(status || '').trim().toLowerCase();
  if (!s) return 'fmct-fuel-status--empty';
  if (s === 'not processed') return 'fmct-fuel-status--not-processed';
  if (s === 'pending') return 'fmct-fuel-status--pending';
  if (s === 'processed') return 'fmct-fuel-status--processed';
  if (s === 'paid') return 'fmct-fuel-status--paid';
  return 'fmct-fuel-status--other';
}

const emptyFuelInvoiceForm = () => ({
  invoiceNo: '',
  month: '',
  year: '',
  project: '',
  fuelAmount: '',
  managementFee: '',
  vat: '',
  po: '',
  paymentStatus: 'Not processed',
  datePaid: '',
  prNoJazzcom: ''
});

/** Convert stored datePaid (DD-Mon-YY / ISO / empty) → YYYY-MM-DD for <input type="date">. */
const fuelDatePaidToInputValue = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const m = raw.match(/^(\d{1,2})[-/ ]([A-Za-z]{3})[-/ ](\d{2}|\d{4})$/);
  if (!m) return '';
  const day = String(Number(m[1])).padStart(2, '0');
  const monIdx = FUEL_INVOICE_MONTH_SHORT.findIndex(
    (s) => s.toLowerCase() === m[2].toLowerCase()
  );
  if (monIdx < 0) return '';
  let year = Number(m[3]);
  if (year < 100) year += year >= 70 ? 1900 : 2000;
  return `${year}-${String(monIdx + 1).padStart(2, '0')}-${day}`;
};

/** Convert YYYY-MM-DD from date picker → DD-Mon-YY (spreadsheet style). */
const fuelDateInputToPaidLabel = (iso) => {
  const raw = String(iso || '').trim();
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '';
  const [y, mo, d] = raw.split('-');
  const mon = FUEL_INVOICE_MONTH_SHORT[Number(mo) - 1];
  if (!mon) return '';
  return `${Number(d)}-${mon}-${String(y).slice(-2)}`;
};

const formatKm = (n) => `${Number(n || 0).toLocaleString('en-US')} KM`;

const formatDriverVehicleLabel = (v) => {
  if (!v) return '';
  return `${v.plate} — ${v.model === 'Rental vehicle' ? v.project : v.model} (${v.driver})`;
};

const formatCostPerKm = (n) => `${(Number(n || 0) * 1000).toFixed(2)} TZS/km`;

const statusLabel = (s) => {
  const map = {
    active: 'Active',
    'due-service': 'Due Service',
    repair: 'In Repair',
    inactive: 'Inactive',
    completed: 'Completed',
    pending: 'Pending',
    'in-progress': 'In Progress'
  };
  return map[s] || s;
};

function resolveLicenseImageSrc(image) {
  if (!image) return '';
  if (/^(data:|https?:|blob:)/.test(image)) return image;
  return `${process.env.PUBLIC_URL || ''}${image}`;
}

const LICENSE_MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function toFullYear(yearStr) {
  const y = String(yearStr);
  if (y.length >= 4) return y.slice(0, 4);
  if (y.length === 2) return `20${y}`;
  return y;
}

function formatLicenseDisplayDate(dateString) {
  if (!dateString) return '';

  const displayMatch = String(dateString).trim().match(/^(\d{1,2})\/([A-Za-z]+)\/(\d{2,4})$/);
  if (displayMatch) {
    const day = displayMatch[1].padStart(2, '0');
    const month = displayMatch[2].charAt(0).toUpperCase() + displayMatch[2].slice(1).toLowerCase();
    const year = toFullYear(displayMatch[3]);
    return `${day}/${month}/${year}`;
  }

  const isoMatch = String(dateString).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const day = isoMatch[3];
    const month = LICENSE_MONTH_NAMES[Number(isoMatch[2]) - 1];
    const year = isoMatch[1];
    return `${day}/${month}/${year}`;
  }

  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return dateString;

  const day = String(parsed.getDate()).padStart(2, '0');
  const month = LICENSE_MONTH_NAMES[parsed.getMonth()];
  const year = String(parsed.getFullYear());
  return `${day}/${month}/${year}`;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function getLicenseImageForPrint(image) {
  const src = resolveLicenseImageSrc(image);
  if (/^(data:|https?:)/i.test(src)) return src;
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  const path = src.startsWith('/') ? src : `/${src}`;
  return `${base}${path}`;
}

function buildDrivingLicensePrintHtml(license, vehicle) {
  const licenseStatus = getLicenseStatus(license.expiryDate);
  const statusStyle =
    licenseStatus.key === 'valid'
      ? 'background:#dcfce7;color:#166534;'
      : licenseStatus.key === 'expiring'
        ? 'background:#fef9c3;color:#854d0e;'
        : 'background:#fee2e2;color:#991b1b;';
  const vehicleStatusStyle =
    vehicle?.displayStatus === 'active'
      ? 'background:#dcfce7;color:#166534;'
      : vehicle?.displayStatus === 'due-service'
        ? 'background:#fef9c3;color:#854d0e;'
        : 'background:#fee2e2;color:#991b1b;';

  const statusDays =
    licenseStatus.days >= 0 ? ` (${licenseStatus.days} days)` : '';

  const vehicleHeader = vehicle
    ? `
      <div class="dl-header">
        <div>
          <h1>${escapeHtml(vehicle.plate)}</h1>
          <p>${escapeHtml(vehicle.model)}</p>
        </div>
        <span class="badge" style="${vehicleStatusStyle}">${escapeHtml(statusLabel(vehicle.displayStatus))}</span>
      </div>
    `
    : '';

  const imageSrc = getLicenseImageForPrint(license.frontImage);

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Driving License — ${escapeHtml(license.fullName)}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      color: #0f172a;
      margin: 0;
      padding: 28px;
      background: #fff;
    }
    .card {
      border: 1px solid #e2e8f0;
      border-radius: 14px;
      overflow: hidden;
      max-width: 900px;
      margin: 0 auto;
    }
    .dl-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      padding: 18px 22px;
      background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
      border-bottom: 1px solid #e2e8f0;
    }
    .dl-header h1 {
      margin: 0;
      font-size: 1.35rem;
      color: #2563EB;
    }
    .dl-header p {
      margin: 4px 0 0;
      color: #64748b;
      font-size: 0.95rem;
    }
    .badge {
      display: inline-block;
      padding: 0.35rem 0.75rem;
      border-radius: 999px;
      font-size: 0.75rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      white-space: nowrap;
    }
    .content { padding: 22px; }
    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 18px;
      margin-bottom: 18px;
    }
    .section {
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 16px;
      background: #fafafa;
    }
    .section h2 {
      margin: 0 0 12px;
      font-size: 0.8rem;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #2563EB;
    }
    dl { margin: 0; }
    dt {
      font-size: 0.72rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #64748b;
      margin-top: 10px;
    }
    dt:first-child { margin-top: 0; }
    dd {
      margin: 4px 0 0;
      font-size: 0.95rem;
      font-weight: 600;
      color: #0f172a;
    }
    .image-section {
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 16px;
      background: #fafafa;
    }
    .image-section h2 {
      margin: 0 0 12px;
      font-size: 0.8rem;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #2563EB;
    }
    .license-image {
      display: block;
      max-width: 360px;
      width: 100%;
      height: auto;
      border-radius: 10px;
      border: 1px solid #e2e8f0;
      background: #fff;
    }
    .footer {
      margin-top: 18px;
      font-size: 0.75rem;
      color: #94a3b8;
      text-align: right;
    }
    @media print {
      body { padding: 12px; }
      .card { border: none; }
    }
    @media (max-width: 700px) {
      .grid { grid-template-columns: 1fr; }
    }
  </style>
</head>
<body>
  <div class="card">
    ${vehicleHeader}
    <div class="content">
      <div class="grid">
        <section class="section">
          <h2>License Details</h2>
          <dl>
            <dt>License number</dt><dd>${escapeHtml(license.licenseNumber)}</dd>
            <dt>Full name</dt><dd>${escapeHtml(license.fullName)}</dd>
            <dt>Class</dt><dd>${escapeHtml(license.licenseClass)}</dd>
            <dt>Status</dt>
            <dd><span class="badge" style="${statusStyle}">${escapeHtml(licenseStatus.label)}${statusDays}</span></dd>
          </dl>
        </section>
        <section class="section">
          <h2>Validity &amp; Authority</h2>
          <dl>
            <dt>Issue date</dt><dd>${escapeHtml(formatLicenseDisplayDate(license.issueDate))}</dd>
            <dt>Expiry date</dt><dd>${escapeHtml(formatLicenseDisplayDate(license.expiryDate))}</dd>
            <dt>Issuing authority</dt><dd>${escapeHtml(license.issuingAuthority)}</dd>
          </dl>
        </section>
      </div>
      <section class="image-section">
        <h2>Driving License — Front View</h2>
        <img class="license-image" src="${imageSrc}" alt="Driving license front" />
      </section>
      <p class="footer">Generated ${escapeHtml(new Date().toLocaleString())}</p>
    </div>
  </div>
  <script>
    window.onload = function () {
      const img = document.querySelector('.license-image');
      if (!img) {
        window.print();
        return;
      }
      if (img.complete) {
        window.print();
      } else {
        img.onload = function () { window.print(); };
        img.onerror = function () { window.print(); };
      }
    };
  </script>
</body>
</html>`;
}

function buildVirPrintHtml(inspection, vehicle) {
  const checks = inspection.checks || {};
  const tickets = inspection.tickets || createDefaultVirTickets();
  const virNo = resolveVirNumber(inspection);
  const overallLabel = getVirResultLabel(inspection.overall);
  const overallStyle =
    inspection.overall === 'fail'
      ? 'background:#fee2e2;color:#991b1b;'
      : 'background:#dcfce7;color:#166534;';
  const attachmentSrc = inspection.attachmentPhoto
    ? getLicenseImageForPrint(inspection.attachmentPhoto)
    : '';

  const checklistSections = VIR_CHECKLIST_CATEGORIES.filter((category) => category.items.length > 0)
    .map((category) => {
      const rows = category.items
        .map((item) => {
          const result = checks[item];
          const label = result ? getVirCheckOptionLabel(result) : '—';
          const cellStyle =
            result === 'fail'
              ? 'color:#b91c1c;font-weight:700;'
              : result === 'pass'
                ? 'color:#166534;font-weight:600;'
                : '';
          return `<tr><td>${escapeHtml(item)}</td><td style="${cellStyle}">${escapeHtml(label)}</td></tr>`;
        })
        .join('');
      return `
        <section class="vir-section">
          <h3>${escapeHtml(category.label)}</h3>
          <table class="vir-table">
            <thead><tr><th>Item</th><th>Result</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </section>
      `;
    })
    .join('');

  const ticketRows = VIR_TICKET_ITEMS.map((ticket) => {
    const entry = tickets[ticket.id] || { hasTicket: 'no', amount: '' };
    const hasTicket = entry.hasTicket === 'yes';
    const amountText = hasTicket
      ? `Yes — TZS ${Number(entry.amount || 0).toLocaleString('en-US')}`
      : 'No';
    return `<tr><td>${escapeHtml(ticket.label)}</td><td>${escapeHtml(amountText)}</td></tr>`;
  }).join('');

  const attachmentSection = attachmentSrc
    ? `
      <section class="vir-attachment">
        <h2>Attachment Photo</h2>
        <img class="vir-attachment-image" src="${attachmentSrc}" alt="Inspection attachment" />
        ${inspection.attachmentPhotoName ? `<p class="vir-attachment-name">${escapeHtml(inspection.attachmentPhotoName)}</p>` : ''}
      </section>
    `
    : '';

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Vehicle Inspection Report — ${escapeHtml(vehicle?.plate || '')}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      color: #0f172a;
      margin: 0;
      padding: 28px;
      background: #fff;
    }
    .card {
      border: 1px solid #e2e8f0;
      border-radius: 14px;
      overflow: hidden;
      max-width: 900px;
      margin: 0 auto;
    }
    .vir-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 1rem;
      padding: 20px 24px;
      background: linear-gradient(135deg, #2563EB 0%, #1e40af 100%);
      color: #fff;
    }
    .vir-header h1 {
      margin: 0 0 6px;
      font-size: 1.35rem;
    }
    .vir-header p {
      margin: 0;
      opacity: 0.92;
      font-size: 0.9rem;
    }
    .badge {
      display: inline-block;
      padding: 6px 12px;
      border-radius: 999px;
      font-size: 0.75rem;
      font-weight: 700;
      white-space: nowrap;
    }
    .content { padding: 22px 24px; }
    .meta-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 14px;
      margin-bottom: 20px;
    }
    .meta-box {
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 12px 14px;
      background: #f8fafc;
    }
    .meta-box dt {
      font-size: 0.68rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #64748b;
      margin-bottom: 4px;
    }
    .meta-box dd {
      margin: 0;
      font-weight: 700;
      font-size: 0.92rem;
    }
    .vir-section { margin-bottom: 18px; }
    .vir-section h3 {
      margin: 0 0 8px;
      font-size: 0.78rem;
      letter-spacing: 0.06em;
      color: #2563EB;
    }
    .vir-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.85rem;
    }
    .vir-table th,
    .vir-table td {
      border: 1px solid #e2e8f0;
      padding: 8px 10px;
      text-align: left;
    }
    .vir-table th {
      background: #f0fdf4;
      font-size: 0.72rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .notes {
      margin-top: 16px;
      padding: 12px 14px;
      border-radius: 10px;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      font-size: 0.88rem;
    }
    .notes h2 {
      margin: 0 0 8px;
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #2563EB;
    }
    .vir-attachment {
      margin-top: 18px;
      padding-top: 16px;
      border-top: 1px solid #e2e8f0;
    }
    .vir-attachment h2 {
      margin: 0 0 10px;
      font-size: 0.8rem;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #2563EB;
    }
    .vir-attachment-image {
      display: block;
      max-width: 420px;
      width: 100%;
      height: auto;
      border-radius: 10px;
      border: 1px solid #e2e8f0;
    }
    .vir-attachment-name {
      margin: 8px 0 0;
      font-size: 0.78rem;
      color: #64748b;
    }
    .footer {
      margin-top: 18px;
      font-size: 0.75rem;
      color: #94a3b8;
      text-align: right;
    }
    @media print {
      body { padding: 12px; }
      .card { border: none; }
    }
    @media (max-width: 700px) {
      .meta-grid { grid-template-columns: 1fr; }
    }
  </style>
</head>
<body>
  <div class="card">
    <div class="vir-header">
      <div>
        <h1>Vehicle Inspection Report</h1>
        <p>${escapeHtml(virNo)} · ${escapeHtml(vehicle?.plate || '')} · ${escapeHtml(vehicle?.model || '')} · ${escapeHtml(vehicle?.driver || '')}</p>
      </div>
      <span class="badge" style="${overallStyle}">${escapeHtml(overallLabel)}</span>
    </div>
    <div class="content">
      <dl class="meta-grid">
        <div class="meta-box"><dt>VIR No.</dt><dd>${escapeHtml(virNo)}</dd></div>
        <div class="meta-box"><dt>Inspection Date</dt><dd>${escapeHtml(formatLicenseDisplayDate(inspection.date))}</dd></div>
        <div class="meta-box"><dt>Inspected Date</dt><dd>${escapeHtml(formatLicenseDisplayDate(inspection.inspectedDate || inspection.date))}</dd></div>
        <div class="meta-box"><dt>Inspector</dt><dd>${escapeHtml(inspection.inspector || '—')}</dd></div>
        <div class="meta-box"><dt>KM Reading</dt><dd>${escapeHtml(inspection.odometer != null ? formatKm(inspection.odometer) : '—')}</dd></div>
        <div class="meta-box"><dt>Project</dt><dd>${escapeHtml(vehicle?.project || '—')}</dd></div>
        <div class="meta-box"><dt>Owner / Rental</dt><dd>${escapeHtml(vehicle?.ownerRentalCompany || '—')}</dd></div>
      </dl>
      ${checklistSections}
      <section class="vir-section">
        <h3>TICKETS</h3>
        <table class="vir-table">
          <thead><tr><th>Ticket</th><th>Details</th></tr></thead>
          <tbody>${ticketRows}</tbody>
        </table>
      </section>
      ${inspection.notes ? `<div class="notes"><h2>Notes</h2><p>${escapeHtml(inspection.notes)}</p></div>` : ''}
      ${attachmentSection}
      <p class="footer">Generated ${escapeHtml(new Date().toLocaleString())}</p>
    </div>
  </div>
  <script>
    window.onload = function () {
      const img = document.querySelector('.vir-attachment-image');
      if (!img) {
        window.print();
        return;
      }
      if (img.complete) {
        window.print();
      } else {
        img.onload = function () { window.print(); };
        img.onerror = function () { window.print(); };
      }
    };
  </script>
</body>
</html>`;
}

function FleetMaintenanceCostTracking() {
  const authSession = readAuthSession();
  const isDriverAccount = authSession?.role === 'driver';
  const navigate = useNavigate();
  const handleDriverLogout = async () => {
    await authLogout();
    navigate('/login', { replace: true });
  };
  const [searchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState(isDriverAccount ? 'drivers' : 'admin');
  const [activeNav, setActiveNav] = useState('dashboard');
  const [search, setSearch] = useState('');
  const [driversOverviewSearch, setDriversOverviewSearch] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [driverFilter, setDriverFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [driverMonthFilter, setDriverMonthFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [costMin, setCostMin] = useState('');
  const [costMax, setCostMax] = useState('');
  const [sortKey, setSortKey] = useState('plate');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [drawerVehicle, setDrawerVehicle] = useState(null);
  const [drawerLicense, setDrawerLicense] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [driverStatusOverrides, setDriverStatusOverrides] = useState({});
  const [vehicleListMeta, setVehicleListMeta] = useState(() => readVehicleListMeta());
  const [driverVehicleId, setDriverVehicleId] = useState('v1');
  const [maintenanceRequest, setMaintenanceRequest] = useState({
    date: new Date().toISOString().slice(0, 10),
    item: '',
    odometer: '',
    location: '',
    channel: 'WhatsApp',
    status: 'reported',
    reportedBy: '',
    remark: '',
    error: ''
  });
  const [maintenanceReports, setMaintenanceReports] = useState(() => readMaintenanceReports());
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [fuelForm, setFuelForm] = useState({
    vehicleId: '',
    date: '',
    previousOdometer: '',
    odometer: '',
    liters: '',
    fuelCost: '',
    paymentType: 'card',
    fuelCardNumber: '',
    cashAttachment: '',
    cashAttachmentName: ''
  });
  const [fuelFormError, setFuelFormError] = useState('');
  const [fuelDialogOpen, setFuelDialogOpen] = useState(false);
  const [fuelRecordOverrides, setFuelRecordOverrides] = useState({});
  const [fuelLogsFromDb, setFuelLogsFromDb] = useState([]);
  const [fuelSaving, setFuelSaving] = useState(false);
  const [fleetFuelInvoices, setFleetFuelInvoices] = useState([]);
  const [fleetFuelInvoicesLoading, setFleetFuelInvoicesLoading] = useState(false);
  const [fleetFuelInvoicesError, setFleetFuelInvoicesError] = useState('');
  const [fleetFuelInvoiceSavingIds, setFleetFuelInvoiceSavingIds] = useState(() => new Set());
  const [fleetFuelInvoiceAdding, setFleetFuelInvoiceAdding] = useState(false);
  const fleetFuelInvoiceSaveTimers = useRef({});
  const fleetFuelInvoiceSnapshotsRef = useRef({});
  const [virChecks, setVirChecks] = useState(() => createDefaultVirChecks());
  const [virTickets, setVirTickets] = useState(() => createDefaultVirTickets());
  const [virKmReading, setVirKmReading] = useState('');
  const [virFormError, setVirFormError] = useState('');
  const [virNotes, setVirNotes] = useState('');
  const [virAttachment, setVirAttachment] = useState('');
  const [virAttachmentName, setVirAttachmentName] = useState('');
  const [virInspectDialogOpen, setVirInspectDialogOpen] = useState(false);
  const [virInspectTargetDate, setVirInspectTargetDate] = useState(null);
  const [virSubmissionOverrides, setVirSubmissionOverrides] = useState({});
  const [virReportsFromDb, setVirReportsFromDb] = useState([]);
  const [allVirReportsFromDb, setAllVirReportsFromDb] = useState([]);
  const [virSaving, setVirSaving] = useState(false);
  const [licenseOverrides, setLicenseOverrides] = useState({});
  const [licensesFromDb, setLicensesFromDb] = useState({});
  const [licenseSaving, setLicenseSaving] = useState(false);
  const [isLicenseEditing, setIsLicenseEditing] = useState(false);
  const [licenseDraft, setLicenseDraft] = useState(null);
  const [driverProfileOverrides, setDriverProfileOverrides] = useState({});
  const [driverProfilesFromDb, setDriverProfilesFromDb] = useState({});
  const [driverProfileSaving, setDriverProfileSaving] = useState(false);
  const [isDriverProfileEditing, setIsDriverProfileEditing] = useState(false);
  const [driverProfileDraft, setDriverProfileDraft] = useState(null);
  const [serviceCardRecordOverrides, setServiceCardRecordOverrides] = useState({});
  const [serviceCardsFromDb, setServiceCardsFromDb] = useState([]);
  const [serviceCardSaving, setServiceCardSaving] = useState(false);
  const [serviceCardDialogOpen, setServiceCardDialogOpen] = useState(false);
  const [serviceCardForm, setServiceCardForm] = useState({
    date: '',
    servicedMileage: '',
    nextServiceKm: '',
    notes: ''
  });
  const [serviceCardFormError, setServiceCardFormError] = useState('');
  const [breakdownRecordOverrides, setBreakdownRecordOverrides] = useState({});
  const [breakdownDialogOpen, setBreakdownDialogOpen] = useState(false);
  const [breakdownForm, setBreakdownForm] = useState({
    date: '',
    location: '',
    odometer: '',
    issue: '',
    attachment: '',
    attachmentName: ''
  });
  const [breakdownFormError, setBreakdownFormError] = useState('');
  const [carRentalRows, setCarRentalRows] = useState(() => readCarRentalsSessionCache() || []);
  const [carRentalsLoading, setCarRentalsLoading] = useState(false);
  const [vehiclePickerOpen, setVehiclePickerOpen] = useState(false);
  const [vehicleSearchQuery, setVehicleSearchQuery] = useState('');
  const vehiclePickerRef = useRef(null);
  const vehicleStatusSaveTimers = useRef({});

  const isAdminView = viewMode === 'admin';

  useEffect(() => {
    if (!isDriverAccount && searchParams.get('mode') === 'drivers') {
      setViewMode('drivers');
    }
  }, [isDriverAccount, searchParams]);

  const visibleNavItems = useMemo(() => {
    if (!isAdminView) return DRIVER_NAV_ITEMS;
    return ADMIN_NAV_ITEMS;
  }, [isAdminView]);

  useEffect(() => {
    if (!isAdminView && activeNav === 'notifications') {
      setActiveNav('dashboard');
    }
  }, [isAdminView, activeNav]);

  const switchViewMode = (mode) => {
    if (isDriverAccount && mode === 'admin') return;
    setViewMode(mode);
    setDrawerVehicle(null);
    setDrawerLicense(null);
    if (mode === 'drivers') {
      setActiveNav('dashboard');
    } else if (
      activeNav === 'vir' ||
      activeNav === 'service-card' ||
      activeNav === 'breakdown' ||
      ['reports', 'settings'].includes(activeNav)
    ) {
      setActiveNav('dashboard');
    }
  };

  const getRecordsForVehicle = useCallback((vehicleId) => {
    return MAINTENANCE_RECORDS.filter((r) => r.vehicleId === vehicleId).sort((a, b) =>
      b.date.localeCompare(a.date)
    );
  }, []);

  const vehicles = useMemo(() => enrichVehicles(), []);
  const allDriversVehicles = useMemo(
    () => buildDriverVehicleListFromCarRentals(carRentalRows, vehicles),
    [carRentalRows, vehicles]
  );

  useEffect(() => {
    try {
      localStorage.setItem(VEHICLE_LIST_META_KEY, JSON.stringify(vehicleListMeta));
    } catch {
      /* ignore quota / private mode */
    }
  }, [vehicleListMeta]);

  useEffect(() => {
    try {
      localStorage.setItem(MAINTENANCE_REPORTS_KEY, JSON.stringify(maintenanceReports));
    } catch {
      /* ignore quota / private mode */
    }
  }, [maintenanceReports]);

  // Load status/remark overrides from Appwrite (backend is source of truth).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchAllVehicleStatuses();
        if (cancelled || !rows.length) return;
        setVehicleListMeta((prev) => {
          const next = { ...prev };
          rows.forEach((row) => {
            const key = normalizeVehicleKey(row.vehicleKey || row.plate);
            if (!key) return;
            const entry = {};
            if (row.status) entry.status = row.status;
            if (row.remark) entry.remark = row.remark;
            if (entry.status || entry.remark) next[key] = entry;
            else delete next[key];
          });
          return next;
        });
      } catch {
        /* offline / collection missing — fall back to local cache */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const persistVehicleStatus = useCallback(
    (vehicle, entry) => {
      const key = getVehicleListKey(vehicle);
      if (!key) return;
      if (vehicleStatusSaveTimers.current[key]) {
        clearTimeout(vehicleStatusSaveTimers.current[key]);
      }
      vehicleStatusSaveTimers.current[key] = setTimeout(() => {
        upsertVehicleStatus({
          vehicleKey: key,
          plate: vehicle.plate || key,
          status: entry.status || '',
          remark: entry.remark || '',
          updatedByEmail: authSession?.email || '',
          updatedByName: authSession?.name || ''
        }).catch(() => {
          /* keep local copy; backend will retry on next edit */
        });
      }, 600);
    },
    [authSession?.email, authSession?.name]
  );

  const updateVehicleListMeta = useCallback(
    (vehicle, patch) => {
      const key = getVehicleListKey(vehicle);
      if (!key) return;
      let persistedEntry = { status: '', remark: '' };
      setVehicleListMeta((prev) => {
        const current = prev[key] || {};
        const nextEntry = {
          status: patch.status !== undefined ? patch.status : current.status,
          remark: patch.remark !== undefined ? patch.remark : (current.remark || '')
        };
        if (!nextEntry.status) delete nextEntry.status;
        if (!nextEntry.remark) delete nextEntry.remark;
        persistedEntry = {
          status: nextEntry.status || '',
          remark: nextEntry.remark || ''
        };
        const next = { ...prev };
        if (!nextEntry.status && !nextEntry.remark) {
          delete next[key];
        } else {
          next[key] = nextEntry;
        }
        return next;
      });
      persistVehicleStatus(vehicle, persistedEntry);
    },
    [persistVehicleStatus]
  );

  const applyVehicleListMeta = useCallback(
    (list) =>
      (list || []).map((v) => {
        const key = getVehicleListKey(v);
        const meta = key ? vehicleListMeta[key] : null;
        if (!meta) return { ...v, remark: v.remark || '' };
        return {
          ...v,
          displayStatus: meta.status || v.displayStatus,
          status: meta.status || v.status,
          remark: meta.remark ?? v.remark ?? ''
        };
      }),
    [vehicleListMeta]
  );

  const getVehicleActivityRank = useCallback((vehicle) => {
    const status = String(vehicle?.displayStatus || vehicle?.status || 'active').toLowerCase();
    if (isInactiveFleetPlate(vehicle?.plate) || status === 'inactive') return 2;
    if (status === 'active' || isActiveFleetPlate(vehicle?.plate)) return 0;
    return 1;
  }, []);

  const isVehicleInactive = useCallback(
    (vehicle) => getVehicleActivityRank(vehicle) === 2,
    [getVehicleActivityRank]
  );

  const driverVehicles = useMemo(() => {
    if (isAdminView) return vehicles;
    let list = buildDriverVehicleListFromCarRentals(carRentalRows, vehicles);
    if (isDriverAccount) {
      list = list.filter((v) => {
        // Prefer surname/key match (e.g. kipruto → only KIPRUTO Shadrack vehicles).
        if (authSession?.driverKey) {
          const key = normalizePersonToken(authSession.driverKey);
          const driver = normalizePersonToken(v.driver);
          if (driver.includes(key)) return true;
        }
        return driverNameMatches(authSession?.name, v.driver);
      });
    }
    // Active vehicles first so drivers land on a working car after login.
    return [...list].sort((a, b) => {
      const rankDiff = getVehicleActivityRank(a) - getVehicleActivityRank(b);
      if (rankDiff !== 0) return rankDiff;
      return String(a.plate || '').localeCompare(String(b.plate || ''), undefined, {
        sensitivity: 'base'
      });
    });
  }, [
    isAdminView,
    carRentalRows,
    vehicles,
    isDriverAccount,
    authSession?.name,
    authSession?.driverKey,
    getVehicleActivityRank
  ]);

  const driverProjectOptions = useMemo(() => {
    if (isAdminView) return ['all'];
    const projects = new Set(
      driverVehicles.map((v) => String(v.project || '').trim()).filter(Boolean)
    );
    return [
      'all',
      ...Array.from(projects).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    ];
  }, [isAdminView, driverVehicles]);

  const visibleDriverVehicles = useMemo(() => {
    if (isAdminView) return driverVehicles;
    const list =
      projectFilter === 'all'
        ? driverVehicles
        : driverVehicles.filter((v) => v.project === projectFilter);
    return [...list].sort((a, b) => {
      const rankDiff = getVehicleActivityRank(a) - getVehicleActivityRank(b);
      if (rankDiff !== 0) return rankDiff;
      return String(a.plate || '').localeCompare(String(b.plate || ''), undefined, {
        sensitivity: 'base'
      });
    });
  }, [isAdminView, driverVehicles, projectFilter, getVehicleActivityRank]);

  const selectedDriverVehicle = useMemo(
    () => visibleDriverVehicles.find((v) => v.id === driverVehicleId) || visibleDriverVehicles[0],
    [visibleDriverVehicles, driverVehicleId]
  );

  const selectedVehicleInactive = isVehicleInactive(selectedDriverVehicle);

  // eslint-disable-next-line no-unused-vars
  const selectedDriverFleetDataId = useMemo(
    () => resolveFleetDataVehicleId(selectedDriverVehicle, vehicles),
    [selectedDriverVehicle, vehicles]
  );
  const adminDriversCards = useMemo(() => {
    let rows = [...allDriversVehicles];
    if (projectFilter !== 'all') rows = rows.filter((v) => v.project === projectFilter);
    const q = driversOverviewSearch.trim().toLowerCase();
    const grouped = rows.reduce((acc, vehicle) => {
      const key = normalizePersonToken(vehicle.driver) || `driver-${vehicle.id}`;
      if (!acc[key]) {
        acc[key] = {
          key,
          driver: vehicle.driver,
          representativeVehicle: vehicle,
          projects: new Set(),
          plateKeys: new Set(),
          plateLabels: new Set(),
          cars: []
        };
      }
      const bucket = acc[key];
      if (vehicle.project) bucket.projects.add(vehicle.project);
      const plateKey = normalizeRegNumber(vehicle.plate || vehicle.id);
      if (!bucket.plateKeys.has(plateKey)) {
        bucket.plateKeys.add(plateKey);
        bucket.plateLabels.add(vehicle.plate || plateKey);
        bucket.cars.push({
          model: vehicle.model || 'Vehicle',
          project: vehicle.project || '—'
        });
      }
      if ((vehicle.odometer || 0) > (bucket.representativeVehicle?.odometer || 0)) {
        bucket.representativeVehicle = vehicle;
      }
      return acc;
    }, {});

    const result = Object.values(grouped)
      .map((entry) => ({
        ...entry,
        vehiclesUsedCount: entry.plateKeys.size,
        plates: Array.from(entry.plateLabels).sort((a, b) =>
          String(a).localeCompare(String(b), undefined, { sensitivity: 'base' })
        ),
        projects: Array.from(entry.projects).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
        cars: entry.cars.sort((a, b) => a.model.localeCompare(b.model, undefined, { sensitivity: 'base' }))
      }))
      .sort((a, b) => a.driver.localeCompare(b.driver, undefined, { sensitivity: 'base' }));

    if (!q) return result;
    return result.filter((entry) => {
      const haystack = [
        entry.driver,
        ...entry.plates,
        ...entry.projects,
        ...entry.cars.map((car) => `${car.model} ${car.project}`)
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [allDriversVehicles, projectFilter, driversOverviewSearch]);
  const adminDriversByProject = useMemo(() => {
    const grouped = adminDriversCards.reduce((acc, entry) => {
      const projectName = projectFilter !== 'all'
        ? projectFilter
        : (entry.projects[0] || 'Unassigned');
      if (!acc[projectName]) acc[projectName] = [];
      acc[projectName].push(entry);
      return acc;
    }, {});

    return Object.entries(grouped)
      .sort((a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }))
      .map(([project, entries]) => ({
        project,
        entries: entries.sort((a, b) =>
          a.driver.localeCompare(b.driver, undefined, { sensitivity: 'base' })
        )
      }));
  }, [adminDriversCards, projectFilter]);
  const getDriverStatus = useCallback(
    (entry, fallbackStatus = 'active') => {
      if (driverStatusOverrides[entry.key]) return driverStatusOverrides[entry.key];

      const { year, month } = getCurrentRentalPeriod();
      if (!driverHasCarRentalInPeriod(entry.driver, carRentalRows, year, month)) {
        return 'inactive';
      }

      const plates = entry.plates || [];
      for (const plate of plates) {
        const metaKey = normalizeVehicleKey(plate);
        const metaStatus = vehicleListMeta[metaKey]?.status;
        if (metaStatus) return metaStatus;
      }
      const repKey = getVehicleListKey(entry.representativeVehicle);
      if (repKey && vehicleListMeta[repKey]?.status) {
        return vehicleListMeta[repKey].status;
      }
      if (plates.some((p) => isInactiveFleetPlate(p))) return 'inactive';
      if (isInactiveFleetPlate(entry.representativeVehicle?.plate)) return 'inactive';
      if (plates.some((p) => isActiveFleetPlate(p))) return 'active';
      if (isActiveFleetPlate(entry.representativeVehicle?.plate)) return 'active';
      return fallbackStatus || 'active';
    },
    [driverStatusOverrides, vehicleListMeta, carRentalRows]
  );

  const updateDriverStatus = useCallback(
    (entry, nextStatus) => {
      const status = String(nextStatus || '').trim();
      if (!entry?.key || !status) return;

      setDriverStatusOverrides((prev) => ({
        ...prev,
        [entry.key]: status
      }));

      const plateKeys = new Set(
        (entry.plates || [])
          .map((p) => normalizeVehicleKey(p))
          .filter(Boolean)
      );
      const repKey = getVehicleListKey(entry.representativeVehicle);
      if (repKey) plateKeys.add(repKey);

      const vehiclesToUpdate = allDriversVehicles.filter((v) => {
        const driverKey = normalizePersonToken(v.driver) || `driver-${v.id}`;
        if (driverKey === entry.key) return true;
        const plateKey = getVehicleListKey(v);
        return plateKey && plateKeys.has(plateKey);
      });

      const seen = new Set();
      vehiclesToUpdate.forEach((vehicle) => {
        const key = getVehicleListKey(vehicle);
        if (!key || seen.has(key)) return;
        seen.add(key);
        updateVehicleListMeta(vehicle, { status });
      });

      // Fallback: at least persist the representative vehicle.
      if (seen.size === 0 && entry.representativeVehicle) {
        updateVehicleListMeta(entry.representativeVehicle, { status });
      }
    },
    [allDriversVehicles, updateVehicleListMeta]
  );

  const filteredVehiclePickerOptions = useMemo(() => {
    const q = vehicleSearchQuery.trim().toLowerCase();
    if (!q) return visibleDriverVehicles;
    return visibleDriverVehicles.filter((v) => {
      const haystack = [v.plate, v.model, v.driver, v.project, formatDriverVehicleLabel(v)]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [visibleDriverVehicles, vehicleSearchQuery]);

  useEffect(() => {
    if (!vehiclePickerOpen) return undefined;
    const onDocClick = (event) => {
      if (vehiclePickerRef.current && !vehiclePickerRef.current.contains(event.target)) {
        setVehiclePickerOpen(false);
        setVehicleSearchQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [vehiclePickerOpen]);

  useEffect(() => {
    setVehiclePickerOpen(false);
    setVehicleSearchQuery('');
  }, [driverVehicleId, projectFilter, isAdminView]);

  const driverVirInspections = useMemo(() => {
    if (!selectedDriverVehicle) return [];
    const fleetDataId = resolveFleetDataVehicleId(selectedDriverVehicle, vehicles);
    // Always schedule Friday VIR rows for the selected vehicle — even when it only
    // exists in Car Rental Invoicing (no fleet mock match / plate typo like T993ESE).
    const virVehicleId = fleetDataId || selectedDriverVehicle.id;
    const rows = getVirForVehicle(virVehicleId, undefined, {
      plate: selectedDriverVehicle.plate,
      driver: selectedDriverVehicle.driver,
      odometer: selectedDriverVehicle.odometer
    });

    const dbByDate = new Map(
      (virReportsFromDb || []).map((r) => [String(r.date || r.inspectionDate), r])
    );

    return rows.map((row) => {
      const key = `${selectedDriverVehicle.id}-${row.date}`;
      const fromDb = dbByDate.get(String(row.date));
      const override = virSubmissionOverrides[key];
      const merged = {
        ...row,
        ...(fromDb
          ? {
              overall: fromDb.overall,
              inspectedDate: fromDb.inspectedDate,
              notes: fromDb.notes,
              odometer: fromDb.odometer,
              checks: fromDb.checks,
              tickets: fromDb.tickets,
              attachmentPhoto: fromDb.attachmentPhoto,
              attachmentPhotoName: fromDb.attachmentPhotoName,
              inspector: fromDb.inspector || row.inspector,
              virNo: fromDb.virNo || row.virNo,
              scheduled: fromDb.overall === 'inspect'
            }
          : {}),
        ...(override || {})
      };
      return {
        ...merged,
        virNo: resolveVirNumber({
          ...merged,
          vehicleId: selectedDriverVehicle.id,
          plate: selectedDriverVehicle.plate
        })
      };
    });
  }, [selectedDriverVehicle, virSubmissionOverrides, vehicles, virReportsFromDb]);

  const pendingVirInspectionCount = useMemo(() => {
    if (selectedVehicleInactive) return 0;
    return driverVirInspections.filter((r) => r.overall === 'inspect').length;
  }, [driverVirInspections, selectedVehicleInactive]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const rows = await fetchAllVirReports();
        if (!cancelled) setAllVirReportsFromDb(rows);
      } catch (err) {
        console.warn('VIR reports (all) load failed:', getVirReportErrorMessage(err));
        if (!cancelled) setAllVirReportsFromDb([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedDriverVehicle) {
      setVirReportsFromDb([]);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const fleetDataId = resolveFleetDataVehicleId(selectedDriverVehicle, vehicles);
        const rows = await fetchVirReportsForVehicle({
          vehicleId: fleetDataId || selectedDriverVehicle.id,
          plate: selectedDriverVehicle.plate
        });
        if (!cancelled) setVirReportsFromDb(rows);
      } catch (err) {
        console.warn('VIR reports load failed:', getVirReportErrorMessage(err));
        if (!cancelled) setVirReportsFromDb([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDriverVehicle?.id, selectedDriverVehicle?.plate, vehicles]);

  useEffect(() => {
    if (!selectedDriverVehicle) {
      setFuelLogsFromDb([]);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const fleetDataId = resolveFleetDataVehicleId(selectedDriverVehicle, vehicles);
        const rows = await fetchFuelLogsForVehicle({
          vehicleId: fleetDataId || selectedDriverVehicle.id,
          plate: selectedDriverVehicle.plate
        });
        if (!cancelled) setFuelLogsFromDb(rows);
      } catch (err) {
        console.warn('Fuel logs load failed:', getFuelLogErrorMessage(err));
        if (!cancelled) setFuelLogsFromDb([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDriverVehicle?.id, selectedDriverVehicle?.plate, vehicles]);

  useEffect(() => {
    if (!isAdminView) return undefined;
    let cancelled = false;
    const load = async () => {
      const showLoading = activeNav === 'fuel' || fleetFuelInvoices.length === 0;
      if (showLoading) setFleetFuelInvoicesLoading(true);
      if (activeNav === 'fuel') setFleetFuelInvoicesError('');
      try {
        const rows = await fetchAllFleetFuelInvoices();
        if (!cancelled) {
          rows.forEach((row) => {
            fleetFuelInvoiceSnapshotsRef.current[row.id] = row;
          });
          setFleetFuelInvoices(rows);
        }
      } catch (err) {
        console.warn('Fleet fuel invoices load failed:', getFleetFuelInvoiceErrorMessage(err));
        if (!cancelled && activeNav === 'fuel') {
          setFleetFuelInvoices([]);
          setFleetFuelInvoicesError(
            getFleetFuelInvoiceErrorMessage(err) ||
              'Could not load fuel invoices. Run npm run setup:fleet-fuel-invoices.'
          );
        }
      } finally {
        if (!cancelled) setFleetFuelInvoicesLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh when entering admin/fuel; length only gates loading UI
  }, [isAdminView, activeNav]);

  useEffect(() => {
    const timersRef = fleetFuelInvoiceSaveTimers;
    return () => {
      Object.values(timersRef.current).forEach((timerId) => {
        clearTimeout(timerId);
      });
    };
  }, []);

  useEffect(() => {
    if (isAdminView) return;
    setMaintenanceRequest({ item: '', notes: '', submitted: false });
    setFuelForm({
      vehicleId: '',
      date: '',
      previousOdometer: '',
      odometer: '',
      liters: '',
      fuelCost: '',
      paymentType: 'card',
      fuelCardNumber: '',
      cashAttachment: '',
      cashAttachmentName: ''
    });
    setFuelFormError('');
    setFuelDialogOpen(false);
    setVirChecks(createDefaultVirChecks());
    setVirTickets(createDefaultVirTickets());
    setVirKmReading('');
    setVirFormError('');
    setVirNotes('');
    setVirAttachment('');
    setVirAttachmentName('');
    setVirInspectDialogOpen(false);
    setVirInspectTargetDate(null);
    setIsLicenseEditing(false);
    setLicenseDraft(null);
    setIsDriverProfileEditing(false);
    setDriverProfileDraft(null);
    setServiceCardDialogOpen(false);
    setServiceCardForm({ date: '', servicedMileage: '', nextServiceKm: '', notes: '' });
    setServiceCardFormError('');
    setBreakdownDialogOpen(false);
    setBreakdownForm({ date: '', location: '', odometer: '', issue: '', attachment: '', attachmentName: '' });
    setBreakdownFormError('');
  }, [driverVehicleId, isAdminView]);

  useEffect(() => {
    if (isAdminView || !visibleDriverVehicles.length) return;
    const preferredActive =
      visibleDriverVehicles.find((v) => getVehicleActivityRank(v) === 0) ||
      visibleDriverVehicles[0];
    const currentInList = visibleDriverVehicles.some((v) => v.id === driverVehicleId);
    // Prefer active vehicle when selection is missing or still on the default placeholder.
    if (!currentInList || driverVehicleId === 'v1') {
      if (preferredActive?.id && preferredActive.id !== driverVehicleId) {
        setDriverVehicleId(preferredActive.id);
      }
    }
  }, [visibleDriverVehicles, driverVehicleId, isAdminView, getVehicleActivityRank]);

  useEffect(() => {
    let cancelled = false;

    const loadCarRentals = async () => {
      setCarRentalsLoading(true);
      try {
        const data = await fetchAllCarRentals();
        if (!cancelled) {
          setCarRentalRows(data);
          writeCarRentalsSessionCache(data);
        }
      } catch (err) {
        console.error('Fleet dashboard: failed to load car rental rows', err);
      } finally {
        if (!cancelled) setCarRentalsLoading(false);
      }
    };

    loadCarRentals();
    return () => {
      cancelled = true;
    };
  }, []);

  // eslint-disable-next-line no-unused-vars
  const getEffectiveDriverProfile = useCallback(
    (vehicleId) => {
      const vehicle = vehicles.find((v) => v.id === vehicleId);
      if (!vehicle) return null;
      const fromDb = driverProfilesFromDb[vehicleId];
      const base = {
        driver: fromDb?.driver || vehicle.driver,
        project: fromDb?.project || vehicle.project,
        phone: fromDb?.phone || vehicle.phone,
        email: fromDb?.email || vehicle.email,
        driverAvatar: fromDb?.driverAvatar || vehicle.driverAvatar,
        driverAvatarName: fromDb?.driverAvatarName || ''
      };
      const override = driverProfileOverrides[vehicleId];
      return override ? { ...base, ...override } : base;
    },
    [vehicles, driverProfileOverrides, driverProfilesFromDb]
  );

  const getDriverProfileWithRental = useCallback(
    (vehicle) => {
      if (!vehicle) return null;
      const fromDb =
        driverProfilesFromDb[vehicle.id] ||
        Object.values(driverProfilesFromDb).find(
          (p) =>
            normalizeRegNumber(p.plate || p.regNumber) ===
            normalizeRegNumber(vehicle.plate)
        );
      const base = {
        driver: fromDb?.driver || vehicle.driver,
        project: fromDb?.project || vehicle.project,
        phone: fromDb?.phone || vehicle.phone || '',
        email: fromDb?.email || vehicle.email || '',
        driverAvatar: fromDb?.driverAvatar || vehicle.driverAvatar,
        driverAvatarName: fromDb?.driverAvatarName || ''
      };
      const override = driverProfileOverrides[vehicle.id];
      const profile = override ? { ...base, ...override } : base;
      return mergeDriverProfileWithCarRental(profile, vehicle, carRentalRows);
    },
    [carRentalRows, driverProfileOverrides, driverProfilesFromDb]
  );

  const getEffectiveLicense = useCallback(
    (vehicleId, plate = '') => {
      const plateNorm = String(plate || '')
        .trim()
        .toUpperCase()
        .replace(/\s/g, '');
      const fromDb =
        licensesFromDb[vehicleId] ||
        (plateNorm
          ? Object.values(licensesFromDb).find(
              (lic) =>
                String(lic.plate || '')
                  .trim()
                  .toUpperCase()
                  .replace(/\s/g, '') === plateNorm
            )
          : null);
      const base = fromDb || getDrivingLicenseForVehicle(vehicleId);
      if (!base) return null;
      const override = licenseOverrides[vehicleId];
      return override ? { ...base, ...override } : base;
    },
    [licenseOverrides, licensesFromDb]
  );

  const drivingLicenseNavAlert = useMemo(() => {
    if (isAdminView || !selectedDriverVehicle) return null;
    const fleetDataId = resolveFleetDataVehicleId(selectedDriverVehicle, vehicles);
    const licenseKey = fleetDataId || selectedDriverVehicle.id;
    const license =
      getEffectiveLicense(licenseKey, selectedDriverVehicle.plate) ||
      getEffectiveLicense(selectedDriverVehicle.id, selectedDriverVehicle.plate);

    if (!license || !String(license.licenseNumber || '').trim()) {
      return { reason: 'missing', label: 'Add driving license' };
    }
    const status = getLicenseStatus(license.expiryDate);
    if (status.key === 'expired') {
      return { reason: 'expired', label: 'Driving license expired' };
    }
    return null;
  }, [isAdminView, selectedDriverVehicle, vehicles, getEffectiveLicense]);

  const serviceCardNavAlert = useMemo(() => {
    if (isAdminView || !selectedDriverVehicle || selectedVehicleInactive) return null;
    const v = selectedDriverVehicle;
    // Service Card uses Appwrite only (plus in-session saves) — not mock maintenance rows.
    const serviceRecords = [
      ...(serviceCardsFromDb || []),
      ...(serviceCardRecordOverrides[v.id] || [])
    ].filter(
      (r) =>
        r.category === 'Service' &&
        (r.vehicleId === v.id ||
          normalizeRegNumber(r.plate) === normalizeRegNumber(v.plate))
    );

    if (serviceRecords.length === 0) {
      return { reason: 'missing', label: 'Add service card' };
    }

    const latestService = [...serviceRecords].sort((a, b) =>
      String(b.date).localeCompare(String(a.date))
    )[0];
    const lastServiceOdometer = latestService?.servicedOdometer ?? 0;
    const nextServiceKm =
      latestService?.nextServiceKm != null && Number.isFinite(Number(latestService.nextServiceKm))
        ? Number(latestService.nextServiceKm)
        : lastServiceOdometer + SERVICE_INTERVAL_KM;
    const virKm = getLatestInspectionOdometer(driverVirInspections, v.odometer);
    if (virKm != null && nextServiceKm - virKm <= 0) {
      return { reason: 'due', label: 'Service due (VIR KM reached next service)' };
    }
    return null;
  }, [
    isAdminView,
    selectedDriverVehicle,
    selectedVehicleInactive,
    serviceCardRecordOverrides,
    serviceCardsFromDb,
    driverVirInspections
  ]);

  useEffect(() => {
    if (!selectedDriverVehicle) {
      setServiceCardsFromDb([]);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const fleetDataId = resolveFleetDataVehicleId(selectedDriverVehicle, vehicles);
        const rows = await fetchServiceCardsForVehicle({
          vehicleId: fleetDataId || selectedDriverVehicle.id,
          plate: selectedDriverVehicle.plate
        });
        if (!cancelled) setServiceCardsFromDb(rows);
      } catch (err) {
        console.warn('Service cards load failed:', getServiceCardErrorMessage(err));
        if (!cancelled) setServiceCardsFromDb([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDriverVehicle?.id, selectedDriverVehicle?.plate, vehicles]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const rows = await fetchAllDrivingLicenses();
        if (cancelled) return;
        const map = {};
        rows.forEach((lic) => {
          if (lic.vehicleId) map[lic.vehicleId] = lic;
        });
        setLicensesFromDb(map);
      } catch (err) {
        console.warn('Driving licenses load failed:', getDrivingLicenseErrorMessage(err));
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedDriverVehicle) return undefined;
    let cancelled = false;
    const loadOne = async () => {
      try {
        const fleetDataId = resolveFleetDataVehicleId(selectedDriverVehicle, vehicles);
        const lic = await fetchDrivingLicenseForVehicle({
          vehicleId: fleetDataId || selectedDriverVehicle.id,
          plate: selectedDriverVehicle.plate
        });
        if (cancelled || !lic) return;
        setLicensesFromDb((prev) => ({
          ...prev,
          [fleetDataId || selectedDriverVehicle.id]: lic,
          [selectedDriverVehicle.id]: lic
        }));
      } catch (err) {
        console.warn('Driving license load failed:', getDrivingLicenseErrorMessage(err));
      }
    };
    loadOne();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDriverVehicle?.id, selectedDriverVehicle?.plate, vehicles]);

  useEffect(() => {
    if (!selectedDriverVehicle) return undefined;
    let cancelled = false;
    const loadProfile = async () => {
      try {
        const fleetDataId = resolveFleetDataVehicleId(selectedDriverVehicle, vehicles);
        const profile = await fetchDriverProfileForVehicle({
          vehicleId: fleetDataId || selectedDriverVehicle.id,
          plate: selectedDriverVehicle.plate,
          email: selectedDriverVehicle.email
        });
        if (cancelled || !profile) return;
        setDriverProfilesFromDb((prev) => ({
          ...prev,
          [fleetDataId || selectedDriverVehicle.id]: profile,
          [selectedDriverVehicle.id]: profile
        }));
      } catch (err) {
        console.warn('Driver profile load failed:', getDriverProfileErrorMessage(err));
      }
    };
    loadProfile();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDriverVehicle?.id, selectedDriverVehicle?.plate, selectedDriverVehicle?.email, vehicles]);

  const adminLicenseRows = useMemo(() => {
    const mockIds = new Set(DRIVING_LICENSES.map((e) => e.vehicleId));
    const fromMock = DRIVING_LICENSES.map((entry) => {
      const vehicle = vehicles.find((v) => v.id === entry.vehicleId);
      const license = getEffectiveLicense(entry.vehicleId, vehicle?.plate);
      return { vehicleId: entry.vehicleId, license, vehicle };
    }).filter((row) => row.license);

    const fromDbOnly = Object.values(licensesFromDb)
      .filter((lic) => lic.vehicleId && !mockIds.has(lic.vehicleId))
      .map((lic) => {
        const vehicle =
          vehicles.find((v) => v.id === lic.vehicleId) ||
          vehicles.find(
            (v) =>
              normalizeRegNumber(v.plate) ===
              normalizeRegNumber(lic.plate)
          );
        return {
          vehicleId: lic.vehicleId,
          license: getEffectiveLicense(lic.vehicleId, lic.plate),
          vehicle
        };
      })
      .filter((row) => row.license);

    return [...fromMock, ...fromDbOnly];
  }, [vehicles, getEffectiveLicense, licensesFromDb]);

  const drawerLicenseData = useMemo(() => {
    if (!drawerLicense?.vehicleId) return null;
    const vehicle = vehicles.find((v) => v.id === drawerLicense.vehicleId);
    const license = getEffectiveLicense(drawerLicense.vehicleId, vehicle?.plate);
    if (!license) return null;
    return { vehicleId: drawerLicense.vehicleId, license, vehicle };
  }, [drawerLicense, getEffectiveLicense, vehicles]);

  const openLicenseDrawer = useCallback((row) => {
    setDrawerVehicle(null);
    setDrawerLicense(row);
  }, []);

  const openVehicleDrawer = useCallback((vehicle) => {
    setDrawerLicense(null);
    setDrawerVehicle(vehicle);
  }, []);

  const startLicenseEdit = useCallback((license) => {
    setLicenseDraft({
      licenseNumber: license.licenseNumber,
      fullName: license.fullName,
      licenseClass: license.licenseClass,
      issueDate: license.issueDate,
      expiryDate: license.expiryDate,
      issuingAuthority: license.issuingAuthority,
      nationalId: license.nationalId || '',
      frontImage: license.frontImage,
      frontImageName: license.frontImageName || ''
    });
    setIsLicenseEditing(true);
  }, []);

  const cancelLicenseEdit = useCallback(() => {
    setIsLicenseEditing(false);
    setLicenseDraft(null);
  }, []);

  const handleLicenseImageUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = () => {
      setLicenseDraft((prev) => ({
        ...(prev || {}),
        frontImage: reader.result,
        frontImageName: file.name
      }));
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  }, []);

  const saveLicenseEdit = useCallback(
    async (vehicleId, vehicleMeta = {}) => {
      if (!licenseDraft || licenseSaving) return;
      const localPayload = {
        licenseNumber: licenseDraft.licenseNumber,
        fullName: licenseDraft.fullName,
        licenseClass: licenseDraft.licenseClass,
        issueDate: licenseDraft.issueDate,
        expiryDate: licenseDraft.expiryDate,
        issuingAuthority: licenseDraft.issuingAuthority,
        nationalId: licenseDraft.nationalId || '',
        frontImage: licenseDraft.frontImage,
        frontImageName: licenseDraft.frontImageName || ''
      };
      setLicenseOverrides((prev) => ({
        ...prev,
        [vehicleId]: localPayload
      }));
      setLicenseSaving(true);
      try {
        const saved = await upsertDrivingLicense({
          vehicleId,
          plate: vehicleMeta.plate || '',
          driver: vehicleMeta.driver || '',
          ...localPayload,
          updatedByEmail: authSession?.email || '',
          updatedByName: authSession?.name || ''
        });
        setLicensesFromDb((prev) => ({
          ...prev,
          [vehicleId]: saved
        }));
        setIsLicenseEditing(false);
        setLicenseDraft(null);
      } catch (err) {
        console.error('Driving license save failed:', getDrivingLicenseErrorMessage(err));
        alert(getDrivingLicenseErrorMessage(err) || 'Failed to save driving license.');
      } finally {
        setLicenseSaving(false);
      }
    },
    [licenseDraft, licenseSaving, authSession?.email, authSession?.name]
  );

  const startDriverProfileEdit = useCallback((profile) => {
    setDriverProfileDraft({
      driver: profile.driver,
      project: profile.project,
      phone: profile.phone,
      email: profile.email,
      driverAvatar: profile.driverAvatar,
      driverAvatarName: profile.driverAvatarName || ''
    });
    setIsDriverProfileEditing(true);
  }, []);

  const cancelDriverProfileEdit = useCallback(() => {
    setIsDriverProfileEditing(false);
    setDriverProfileDraft(null);
  }, []);

  const handleDriverAvatarUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = () => {
      setDriverProfileDraft((prev) => ({
        ...(prev || {}),
        driverAvatar: reader.result,
        driverAvatarName: file.name
      }));
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  }, []);

  const saveDriverProfileEdit = useCallback(
    async (vehicleId, vehicleMeta = {}) => {
      if (!driverProfileDraft || driverProfileSaving) return;
      const localPayload = {
        driver: driverProfileDraft.driver,
        project: driverProfileDraft.project,
        phone: driverProfileDraft.phone,
        email: driverProfileDraft.email,
        driverAvatar: driverProfileDraft.driverAvatar,
        driverAvatarName: driverProfileDraft.driverAvatarName || ''
      };
      setDriverProfileOverrides((prev) => ({
        ...prev,
        [vehicleId]: localPayload
      }));
      setDriverProfileSaving(true);
      try {
        const saved = await upsertDriverProfile({
          vehicleId,
          plate: vehicleMeta.plate || '',
          driverKey: authSession?.driverKey || '',
          regNumber: vehicleMeta.plate || '',
          rentalCompany: vehicleMeta.ownerRentalCompany || vehicleMeta.rentalCompany || '',
          ...localPayload,
          updatedByEmail: authSession?.email || '',
          updatedByName: authSession?.name || ''
        });
        setDriverProfilesFromDb((prev) => ({
          ...prev,
          [vehicleId]: saved
        }));
        setIsDriverProfileEditing(false);
        setDriverProfileDraft(null);
      } catch (err) {
        console.error('Driver profile save failed:', getDriverProfileErrorMessage(err));
        alert(getDriverProfileErrorMessage(err) || 'Failed to save driver profile.');
      } finally {
        setDriverProfileSaving(false);
      }
    },
    [driverProfileDraft, driverProfileSaving, authSession?.email, authSession?.name, authSession?.driverKey]
  );

  const handleVirAttachmentUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = () => {
      setVirAttachment(reader.result);
      setVirAttachmentName(file.name);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  }, []);

  const handleFuelAttachmentUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = () => {
      setFuelForm((prev) => ({
        ...prev,
        cashAttachment: reader.result,
        cashAttachmentName: file.name
      }));
      setFuelFormError('');
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  }, []);

  const handleBreakdownAttachmentUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = () => {
      setBreakdownForm((prev) => ({
        ...prev,
        attachment: reader.result,
        attachmentName: file.name
      }));
      setBreakdownFormError('');
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  }, []);

  const exportVirPdf = useCallback((inspection, vehicle) => {
    if (!inspection || !vehicle || inspection.overall === 'inspect') return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(buildVirPrintHtml(inspection, vehicle));
    win.document.close();
    win.focus();
  }, []);

  const notifications = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const scopeVehicles = isDriverAccount ? driverVehicles : vehicles;
    const scopeIds = new Set(scopeVehicles.map((v) => v.id));
    const scopePlates = new Set(
      scopeVehicles.map((v) => normalizeRegNumber(v.plate)).filter(Boolean)
    );
    const inScope = (vehicleId, plate) => {
      if (!isDriverAccount) return true;
      if (vehicleId && scopeIds.has(vehicleId)) return true;
      const plateNorm = normalizeRegNumber(plate);
      return plateNorm ? scopePlates.has(plateNorm) : false;
    };
    const findVehicle = (vehicleId, plate) =>
      vehicles.find((v) => v.id === vehicleId) ||
      scopeVehicles.find((v) => v.id === vehicleId) ||
      vehicles.find((v) => normalizeRegNumber(v.plate) === normalizeRegNumber(plate)) ||
      null;

    // Fleet alerts (service, cost, insurance, road licence) plus VIR / breakdown / DL below.
    const items = [...getNotifications(scopeVehicles)];

    // --- Failed vehicle inspections (VIR not okay) ---
    const failedByKey = new Map();
    const rememberFail = (row) => {
      if (!row || row.overall !== 'fail') return;
      if (!inScope(row.vehicleId, row.plate)) return;
      const key = `${row.vehicleId || ''}|${normalizeRegNumber(row.plate) || ''}|${row.date || row.inspectionDate || ''}`;
      if (!failedByKey.has(key)) failedByKey.set(key, row);
    };
    (VIR_INSPECTIONS || []).forEach(rememberFail);
    (allVirReportsFromDb || []).forEach(rememberFail);
    Object.entries(virSubmissionOverrides || {}).forEach(([overrideKey, override]) => {
      if (!override || override.overall !== 'fail') return;
      const vehicleId = String(override.vehicleId || overrideKey.split('-')[0] || '');
      rememberFail({
        ...override,
        id: override.id || `vir-override-${overrideKey}`,
        vehicleId,
        date: override.date || override.inspectionDate || today
      });
    });

    failedByKey.forEach((row, key) => {
      const vehicle = findVehicle(row.vehicleId, row.plate);
      const plate = vehicle?.plate || row.plate || row.vehicleId || 'Vehicle';
      const driver = vehicle?.driver || row.driver || row.inspector || '—';
      const failedItems = Object.entries(row.checks || {})
        .filter(([, result]) => result === 'fail')
        .map(([item]) => item);
      const failDetail =
        failedItems.length > 0
          ? `Failed: ${failedItems.slice(0, 3).join(', ')}${failedItems.length > 3 ? '…' : ''}`
          : row.notes || 'Inspection result is not okay';
      items.push({
        id: `vir-fail-${key}`,
        type: 'danger',
        title: 'Vehicle inspection not okay',
        message: `${plate} (${driver}) — ${row.date || row.inspectionDate || '—'} · ${failDetail}`,
        date: row.date || row.inspectionDate || today
      });
    });

    // --- Open breakdown reports ---
    const breakdownById = new Map();
    (BREAKDOWN_RECORDS || []).forEach((r) => {
      if (r?.id) breakdownById.set(r.id, r);
    });
    Object.values(breakdownRecordOverrides || {}).forEach((list) => {
      (list || []).forEach((r) => {
        if (r?.id) breakdownById.set(r.id, r);
      });
    });
    Array.from(breakdownById.values())
      .filter((r) => r.status === 'reported' || r.status === 'under-repair')
      .filter((r) => inScope(r.vehicleId, r.plate))
      .forEach((r) => {
        const vehicle = findVehicle(r.vehicleId, r.plate);
        const plate = vehicle?.plate || r.plate || r.vehicleId || 'Vehicle';
        const driver = vehicle?.driver || r.reportedBy || '—';
        const statusLabel = getBreakdownStatusLabel(r.status);
        items.push({
          id: `breakdown-${r.id}`,
          type: r.status === 'reported' ? 'danger' : 'warning',
          title: 'Breakdown reported',
          message: `${plate} (${driver}) — ${r.issue || 'Breakdown'} · ${statusLabel}${r.location ? ` · ${r.location}` : ''}`,
          date: r.date || today
        });
      });

    // --- Expired / expiring driving licenses ---
    const licenseRows = isAdminView
      ? adminLicenseRows
      : driverVehicles.map((v) => {
          const fleetDataId = resolveFleetDataVehicleId(v, vehicles);
          return {
            vehicleId: fleetDataId || v.id,
            vehicle: v,
            license: getEffectiveLicense(fleetDataId || v.id, v.plate)
          };
        }).filter((row) => row.license);

    licenseRows.forEach((row) => {
      const license = row.license;
      if (!license?.expiryDate) return;
      const status = getLicenseStatus(license.expiryDate);
      if (status.key !== 'expired' && status.key !== 'expiring') return;
      const plate = row.vehicle?.plate || license.plate || row.vehicleId || '—';
      const name = license.fullName || row.vehicle?.driver || 'Driver';
      items.push({
        id: `dl-${row.vehicleId || plate}`,
        type: status.key === 'expired' ? 'danger' : 'warning',
        title: status.key === 'expired' ? 'Driving license expired' : 'Driving license expiring soon',
        message:
          status.key === 'expired'
            ? `${name} · ${plate} — expired ${license.expiryDate}`
            : `${name} · ${plate} — expires ${license.expiryDate} (${status.days} days)`,
        date: license.expiryDate || today
      });
    });

    const rank = (type) => (type === 'danger' ? 0 : type === 'alert' ? 1 : type === 'warning' ? 2 : 3);
    return items.sort((a, b) => {
      const byType = rank(a.type) - rank(b.type);
      if (byType !== 0) return byType;
      return String(b.date || '').localeCompare(String(a.date || ''));
    });
  }, [
    vehicles,
    driverVehicles,
    isAdminView,
    isDriverAccount,
    allVirReportsFromDb,
    virSubmissionOverrides,
    breakdownRecordOverrides,
    adminLicenseRows,
    getEffectiveLicense
  ]);

  const upcoming = useMemo(() => getUpcomingMaintenance(vehicles), [vehicles]);
  const categoryData = useMemo(() => getCategoryBreakdown(), []);

  const kpis = useMemo(() => {
    const totalCost = MAINTENANCE_RECORDS.reduce((s, r) => s + r.cost, 0);
    const totalKm = vehicles.reduce((s, v) => s + v.odometer, 0);
    const dueCount = vehicles.filter((v) => v.nextServiceDue.isDue || v.nextServiceDue.isSoon).length;
    const openRepairs = MAINTENANCE_RECORDS.filter(
      (r) => r.status === 'pending' || r.status === 'in-progress'
    ).length;

    // Same fleet source + project filter as the Vehicles table.
    let fleetList = applyVehicleListMeta(allDriversVehicles);
    if (projectFilter !== 'all') {
      fleetList = fleetList.filter((v) => v.project === projectFilter);
    }

    const drivers = new Set(
      fleetList
        .filter((v) => getVehicleActivityRank(v) !== 2)
        .map((v) => v.driver)
        .filter(Boolean)
    ).size;

    let activeVehicles = 0;
    let inactiveVehicles = 0;
    fleetList.forEach((v) => {
      if (getVehicleActivityRank(v) === 2) inactiveVehicles += 1;
      else activeVehicles += 1;
    });

    return {
      totalVehicles: fleetList.length,
      activeVehicles,
      inactiveVehicles,
      totalCost,
      dueForService: dueCount,
      avgCostPerKm: totalKm ? totalCost / totalKm : 0,
      openRepairs,
      activeDrivers: drivers
    };
  }, [
    vehicles,
    allDriversVehicles,
    projectFilter,
    applyVehicleListMeta,
    getVehicleActivityRank
  ]);

  const driverOptions = useMemo(
    () => ['all', ...new Set(allDriversVehicles.map((v) => v.driver).filter(Boolean))],
    [allDriversVehicles]
  );
  const projectOptions = useMemo(
    () => [
      'all',
      ...new Set([
        ...FUEL_INVOICE_PROJECTS,
        ...allDriversVehicles
          .map((v) => String(v.project || '').trim())
          .filter(Boolean),
        ...fleetFuelInvoices
          .map((r) => String(r.project || '').trim())
          .filter(Boolean)
      ])
    ],
    [allDriversVehicles, fleetFuelInvoices]
  );
  const topnavProjectOptions = !isDriverAccount ? projectOptions : driverProjectOptions;
  const fuelInvoiceProjectOptions = useMemo(
    () => projectOptions.filter((p) => p !== 'all'),
    [projectOptions]
  );
  const fuelInvoiceYearOptions = useMemo(
    () => buildFuelInvoiceYearOptions(fleetFuelInvoices.map((r) => r.year)),
    [fleetFuelInvoices]
  );

  const driverMonthFilterOptions = useMemo(() => {
    const keys = new Set();
    const now = new Date();
    for (let i = 0; i < 18; i += 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      keys.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    (fuelLogsFromDb || []).forEach((r) => {
      const date = String(r.fillDate || r.date || '').trim();
      const m = date.match(/^(\d{4})-(\d{2})/);
      if (m) keys.add(`${m[1]}-${m[2]}`);
    });
    const monthOptions = [...keys]
      .sort((a, b) => b.localeCompare(a))
      .map((key) => {
        const [y, mo] = key.split('-');
        const monthIdx = Number(mo) - 1;
        return {
          value: key,
          label: `${FUEL_INVOICE_MONTH_SHORT[monthIdx] || mo} ${y}`
        };
      });
    return [{ value: 'all', label: 'All months' }, ...monthOptions];
  }, [fuelLogsFromDb]);

  const driverFuelSummary = useMemo(() => {
    const logs = (fuelLogsFromDb || []).filter((r) => {
      if (driverMonthFilter === 'all') return true;
      const date = String(r.fillDate || r.date || '').trim();
      return date.startsWith(driverMonthFilter);
    });
    let liters = 0;
    let amount = 0;
    let km = 0;
    logs.forEach((r) => {
      liters += Number(r.liters) || 0;
      amount += Number(r.fuelCost) || 0;
      const traveled = Number(r.kmTraveled);
      if (Number.isFinite(traveled) && traveled > 0) {
        km += traveled;
        return;
      }
      const previous = Number(r.previousOdometer);
      const current = Number(r.odometer);
      if (Number.isFinite(previous) && Number.isFinite(current) && current >= previous) {
        km += current - previous;
      }
    });
    const avgConsumption =
      liters > 0 && km > 0 ? km / liters : null;
    return {
      liters,
      amount,
      km,
      avgConsumption,
      fillCount: logs.length
    };
  }, [fuelLogsFromDb, driverMonthFilter]);

  /** Monthly fuel invoice totals for the dashboard trend chart (respects project filter). */
  const fuelInvoiceTrendData = useMemo(
    () => buildFuelInvoiceTrendData(fleetFuelInvoices, { projectFilter }),
    [fleetFuelInvoices, projectFilter]
  );

  const plateOptions = useMemo(
    () => ['all', ...allDriversVehicles.map((v) => v.plate).filter(Boolean)],
    [allDriversVehicles]
  );

  const filteredVehicles = useMemo(() => {
    // Admin Vehicles tab uses the live Car Rental fleet (same source as Drivers).
    let rows = applyVehicleListMeta(allDriversVehicles);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (v) =>
          String(v.plate || '').toLowerCase().includes(q) ||
          String(v.model || '').toLowerCase().includes(q) ||
          String(v.driver || '').toLowerCase().includes(q) ||
          String(v.project || '').toLowerCase().includes(q) ||
          String(v.ownerRentalCompany || '').toLowerCase().includes(q) ||
          String(v.remark || '').toLowerCase().includes(q)
      );
    }
    if (vehicleFilter !== 'all') rows = rows.filter((v) => v.plate === vehicleFilter);
    if (driverFilter !== 'all') rows = rows.filter((v) => v.driver === driverFilter);
    if (projectFilter !== 'all') rows = rows.filter((v) => v.project === projectFilter);

    rows.sort((a, b) => {
      const rankDiff = getVehicleActivityRank(a) - getVehicleActivityRank(b);
      if (rankDiff !== 0) return rankDiff;

      let av = a[sortKey];
      let bv = b[sortKey];
      if (sortKey === 'totalCost' || sortKey === 'odometer' || sortKey === 'costPerKm') {
        av = Number(av) || 0;
        bv = Number(bv) || 0;
      } else {
        av = String(av || '').toLowerCase();
        bv = String(bv || '').toLowerCase();
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return String(a.plate || '').localeCompare(String(b.plate || ''), undefined, {
        sensitivity: 'base'
      });
    });
    return rows;
  }, [
    allDriversVehicles,
    applyVehicleListMeta,
    search,
    vehicleFilter,
    driverFilter,
    projectFilter,
    sortKey,
    sortDir,
    getVehicleActivityRank
  ]);

  const filteredMaintenance = useMemo(() => {
    let rows = MAINTENANCE_RECORDS.map((r) => {
      const v = vehicles.find((x) => x.id === r.vehicleId);
      return { ...r, plate: v?.plate, driver: v?.driver, project: v?.project };
    });
    if (vehicleFilter !== 'all') rows = rows.filter((r) => r.plate === vehicleFilter);
    if (driverFilter !== 'all') rows = rows.filter((r) => r.driver === driverFilter);
    if (projectFilter !== 'all') rows = rows.filter((r) => r.project === projectFilter);
    if (dateFrom) rows = rows.filter((r) => r.date >= dateFrom);
    if (dateTo) rows = rows.filter((r) => r.date <= dateTo);
    const min = costMin ? Number(costMin) : null;
    const max = costMax ? Number(costMax) : null;
    if (min != null && !Number.isNaN(min)) rows = rows.filter((r) => r.cost >= min);
    if (max != null && !Number.isNaN(max)) rows = rows.filter((r) => r.cost <= max);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) =>
          r.item.toLowerCase().includes(q) ||
          r.supplier.toLowerCase().includes(q) ||
          r.invoiceNumber.toLowerCase().includes(q) ||
          (r.plate || '').toLowerCase().includes(q)
      );
    }
    return rows.sort((a, b) => b.date.localeCompare(a.date));
  }, [vehicles, vehicleFilter, driverFilter, projectFilter, dateFrom, dateTo, costMin, costMax, search]);

  const totalPages = Math.max(1, Math.ceil(filteredVehicles.length / PAGE_SIZE));
  const pagedVehicles = filteredVehicles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const drawerRecords = useMemo(() => {
    if (!drawerVehicle) return [];
    return MAINTENANCE_RECORDS.filter((r) => r.vehicleId === drawerVehicle.id).sort((a, b) =>
      b.date.localeCompare(a.date)
    );
  }, [drawerVehicle]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const exportVehiclesExcel = useCallback(() => {
    const data = filteredVehicles.map((v) => ({
      'Plate Number': v.plate,
      Vehicle: v.model,
      Driver: v.driver,
      Project: v.project,
      'Rental company': v.ownerRentalCompany || '',
      Odometer: v.odometer,
      'Total Cost': v.totalCost,
      'Cost Per KM': v.costPerKm,
      Status: statusLabel(v.displayStatus),
      Remark: v.remark || ''
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Vehicles');
    XLSX.writeFile(wb, 'fleet-maintenance-vehicles.xlsx');
  }, [filteredVehicles]);

  const exportMaintenanceExcel = useCallback(() => {
    const data = filteredMaintenance.map((r) => ({
      Date: r.date,
      Plate: r.plate,
      Item: r.item,
      Category: r.category,
      Supplier: r.supplier,
      Cost: r.cost,
      'Invoice Number': r.invoiceNumber,
      Status: statusLabel(r.status)
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Maintenance');
    XLSX.writeFile(wb, 'fleet-maintenance-records.xlsx');
  }, [filteredMaintenance]);

  const exportPdf = useCallback(() => {
    const win = window.open('', '_blank');
    if (!win) return;
    const rows = filteredVehicles
      .map(
        (v) =>
          `<tr><td>${v.plate || ''}</td><td>${v.model || ''}</td><td>${v.driver || ''}</td><td>${v.project || ''}</td><td>${v.ownerRentalCompany || ''}</td><td>${formatKm(v.odometer)}</td><td>${formatCurrency(v.totalCost)}</td><td>${statusLabel(v.displayStatus || 'active')}</td><td>${String(v.remark || '').replace(/</g, '&lt;')}</td></tr>`
      )
      .join('');
    win.document.write(`
      <!DOCTYPE html><html><head><title>Fleet Dashboard Report</title>
      <style>body{font-family:Arial,sans-serif;padding:24px;color:#111}h1{color:#2563EB}
      table{width:100%;border-collapse:collapse;margin-top:16px}th,td{border:1px solid #ddd;padding:8px;text-align:left}
      th{background:#f0fdf4}</style></head><body>
      <h1>Fleet Dashboard</h1>
      <p>Generated ${new Date().toLocaleString()} · ${filteredVehicles.length} vehicle(s)</p>
      <table><thead><tr><th>Plate</th><th>Vehicle</th><th>Driver</th><th>Project</th><th>Rental company</th><th>Odometer</th><th>Total Cost</th><th>Status</th><th>Remark</th></tr></thead>
      <tbody>${rows}</tbody></table></body></html>`);
    win.document.close();
    win.focus();
    win.print();
  }, [filteredVehicles]);

  const exportDrivingLicensePdf = useCallback((license, vehicle) => {
    if (!license) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(buildDrivingLicensePrintHtml(license, vehicle));
    win.document.close();
    win.focus();
  }, []);

  const clearFilters = () => {
    setSearch('');
    setVehicleFilter('all');
    setDriverFilter('all');
    setProjectFilter('all');
    setDateFrom('');
    setDateTo('');
    setCostMin('');
    setCostMax('');
    setPage(1);
  };

  const renderKpis = () => {
    const vehiclesCard = {
      type: 'vehicles',
      label: isAdminView ? 'Total Vehicles' : 'Assigned Vehicles',
      icon: Car,
      accent: '#3b82f6'
    };
    const adminKpis = [
      vehiclesCard,
      { label: 'Avg Cost Per KM', value: formatCostPerKm(kpis.avgCostPerKm), meta: 'Fleet average', icon: LineChartIcon, accent: '#0ea5e9' },
      { label: 'Open Repairs', value: kpis.openRepairs, meta: 'In progress', icon: ClipboardList, accent: '#f59e0b' },
      { label: 'Active Drivers', value: kpis.activeDrivers, meta: 'On duty', icon: Users, accent: '#10b981' }
    ];
    const driverKpis = [
      vehiclesCard,
      { label: 'Due for Service', value: kpis.dueForService, meta: 'Service interval', icon: Wrench, accent: '#f59e0b' },
      { label: 'Open Repairs', value: kpis.openRepairs, meta: 'In progress', icon: ClipboardList, accent: '#f59e0b' },
      { label: 'Active Drivers', value: kpis.activeDrivers, meta: 'On duty', icon: Users, accent: '#10b981' }
    ];
    const items = isAdminView ? adminKpis : driverKpis;
    return (
      <div className="fmct-summary-cards">
        {items.map((k) => {
          const Icon = k.icon;
          const isVehicles = k.type === 'vehicles';
          return (
            <div key={k.label} className="fmct-summary-card" style={{ '--cat-accent': k.accent }}>
              <div className="fmct-summary-card__top">
                <span className="fmct-summary-card__icon" aria-hidden>
                  <Icon className="h-4 w-4" />
                </span>
                <span className="fmct-summary-card__label">{k.label}</span>
              </div>
              {isVehicles ? (
                <div className="fmct-summary-card__count">
                  <span className="fmct-summary-card__count-active">{kpis.activeVehicles} Active</span>
                  <span className="fmct-summary-card__count-sep"> · </span>
                  <span className="fmct-summary-card__count-inactive">{kpis.inactiveVehicles} Inactive</span>
                </div>
              ) : (
                <div className="fmct-summary-card__count">{k.meta}</div>
              )}
              <div className="fmct-summary-card__amount">{isVehicles ? kpis.totalVehicles : k.value}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCharts = () => {
    if (!isAdminView) return null;
    const fuelTrendSubtitle =
      projectFilter === 'all'
        ? 'Monthly fuel invoice totals (all projects)'
        : `Monthly fuel invoice totals · ${projectFilter}`;

    return (
    <div className="fmct-charts-grid">
      <FuelInvoiceTrendChart
        data={fuelInvoiceTrendData}
        loading={fleetFuelInvoicesLoading}
        subtitle={fuelTrendSubtitle}
        emptyMessage="No fuel invoice amounts yet. Add invoices under Fuel."
      />

      <div className="fmct-card fmct-chart-card fmct-chart-pie">
        <h3>Cost By Category</h3>
        <p className="fmct-card-sub">Spend distribution</p>
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie
              data={categoryData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={90}
              paddingAngle={2}
            >
              {categoryData.map((entry) => (
                <Cell key={entry.name} fill={CATEGORY_COLORS[entry.name] || '#94a3b8'} />
              ))}
            </Pie>
            <Tooltip formatter={(v) => formatCurrency(v)} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
    );
  };

  const renderNotifications = () => (
    <div className="fmct-card fmct-notifications-page">
      <div className="fmct-notifications-page-header">
        <div>
          <h3>
            <Bell className="h-4 w-4" /> Notifications
          </h3>
          <p className="fmct-card-sub">
            Service, inspections, breakdowns, licenses, insurance, and cost alerts
          </p>
        </div>
        {notifications.length > 0 && (
          <span className="fmct-notifications-count-pill" aria-label={`${notifications.length} notifications`}>
            {notifications.length > 99 ? '99+' : notifications.length}
          </span>
        )}
      </div>
      <ul className="fmct-notify-list">
        {notifications.length === 0 ? (
          <li className="fmct-notify-info">
            <div className="fmct-notify-title">All clear</div>
            <div className="fmct-notify-msg">No alerts right now.</div>
          </li>
        ) : (
          notifications.map((n) => (
            <li key={n.id} className={`fmct-notify-${n.type || 'info'}`}>
              <div className="fmct-notify-title">{n.title}</div>
              <div className="fmct-notify-msg">{n.message}</div>
              {n.date ? <div className="fmct-notify-date">{n.date}</div> : null}
            </li>
          ))
        )}
      </ul>
    </div>
  );

  const renderWidgets = () => (
    <div className="fmct-widgets-grid">
      <div className="fmct-card">
        <h3>Upcoming Maintenance</h3>
        <p className="fmct-card-sub">Service due based on odometer (5,000 KM interval)</p>
        <ul className="fmct-upcoming-list">
          {upcoming.map((u) => (
            <li key={u.vehicleId} className={u.isDue ? 'fmct-upcoming-due' : ''}>
              <div>
                <strong>{u.plate}</strong> — {u.driver}
                <span className="fmct-muted"> {u.model}</span>
              </div>
              <div className="fmct-upcoming-meta">
                {u.isDue ? (
                  <span className="fmct-badge fmct-badge-danger">Overdue</span>
                ) : (
                  <span className="fmct-badge fmct-badge-warn">{u.kmRemaining} KM to go</span>
                )}
                <span>Next at {formatKm(u.nextKm)}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  const renderFilters = () => (
    <div className={`fmct-filters ${showFilters ? '' : 'fmct-filters-collapsed'}`}>
      <div className="fmct-filters-header">
        <span>
          <Filter className="h-4 w-4" /> Filters
        </span>
        <button type="button" className="fmct-link-btn" onClick={() => setShowFilters((s) => !s)}>
          {showFilters ? 'Hide' : 'Show'}
        </button>
      </div>
      {showFilters && (
        <div className="fmct-filters-grid">
          <label>
            Vehicle
            <select value={vehicleFilter} onChange={(e) => { setVehicleFilter(e.target.value); setPage(1); }}>
              {plateOptions.map((p) => (
                <option key={p} value={p}>{p === 'all' ? 'All vehicles' : p}</option>
              ))}
            </select>
          </label>
          <label>
            Driver
            <select value={driverFilter} onChange={(e) => { setDriverFilter(e.target.value); setPage(1); }}>
              {driverOptions.map((d) => (
                <option key={d} value={d}>{d === 'all' ? 'All drivers' : d}</option>
              ))}
            </select>
          </label>
          <label>
            Project
            <select value={projectFilter} onChange={(e) => { setProjectFilter(e.target.value); setPage(1); }}>
              {projectOptions.map((p) => (
                <option key={p} value={p}>{p === 'all' ? 'All projects' : p}</option>
              ))}
            </select>
          </label>
          <label>
            Date from
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </label>
          <label>
            Date to
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </label>
          {isAdminView && (
            <>
              <label>
                Min cost
                <input type="number" placeholder="0" value={costMin} onChange={(e) => setCostMin(e.target.value)} />
              </label>
              <label>
                Max cost
                <input type="number" placeholder="Any" value={costMax} onChange={(e) => setCostMax(e.target.value)} />
              </label>
            </>
          )}
          <div className="fmct-filter-actions">
            <button type="button" className="fmct-btn fmct-btn-ghost" onClick={clearFilters}>Clear all</button>
          </div>
        </div>
      )}
    </div>
  );

  const vehicleColumns = isAdminView
    ? [
        ['plate', 'Plate Number'],
        ['model', 'Vehicle'],
        ['driver', 'Driver'],
        ['project', 'Project'],
        ['ownerRentalCompany', 'Rental company'],
        ['odometer', 'Odometer'],
        ['totalCost', 'Total Cost'],
        ['costPerKm', 'Cost / KM'],
        ['displayStatus', 'Status'],
        ['remark', 'Remark']
      ]
    : [
        ['plate', 'Plate Number'],
        ['model', 'Vehicle'],
        ['driver', 'Driver'],
        ['project', 'Project'],
        ['odometer', 'Odometer'],
        ['displayStatus', 'Status']
      ];

  const renderVehicleTable = () => (
    <div className="fmct-card fmct-table-card">
      <div className="fmct-table-toolbar">
        <div className="fmct-search">
          <Search className="h-4 w-4" />
          <input
            type="search"
            placeholder="Search plate, driver, project, company, remark…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        {isAdminView && (
          <div className="fmct-toolbar-actions">
            <button type="button" className="fmct-btn fmct-btn-outline" onClick={exportVehiclesExcel}>
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </button>
            <button type="button" className="fmct-btn fmct-btn-outline" onClick={exportPdf}>
              <FileText className="h-4 w-4" /> PDF
            </button>
          </div>
        )}
      </div>
      <div className="fmct-table-wrap">
        <table className="fmct-table fmct-table--vehicles">
          <thead>
            <tr>
              {vehicleColumns.map(([key, label]) => (
                <th key={key}>
                  {key === 'remark' ? (
                    <span className="fmct-th-static">{label}</span>
                  ) : (
                    <button type="button" className="fmct-sort-btn" onClick={() => toggleSort(key)}>
                      {label} <ArrowUpDown className="h-4 w-4" />
                    </button>
                  )}
                </th>
              ))}
              {isAdminView && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {pagedVehicles.length === 0 ? (
              <tr>
                <td colSpan={vehicleColumns.length + (isAdminView ? 1 : 0)} className="fmct-table-empty">
                  {allDriversVehicles.length === 0
                    ? 'No vehicles loaded yet. Vehicles come from Car Rental Invoicing.'
                    : 'No vehicles match the current filters.'}
                </td>
              </tr>
            ) : (
              pagedVehicles.map((v) => {
                const statusValue = v.displayStatus || 'active';
                return (
                  <tr key={v.id}>
                    <td><strong>{v.plate}</strong></td>
                    <td>{v.model || '—'}</td>
                    <td>{v.driver || '—'}</td>
                    <td>{v.project || '—'}</td>
                    {isAdminView && <td>{v.ownerRentalCompany || '—'}</td>}
                    <td>{formatKm(v.odometer)}</td>
                    {isAdminView && <td>{formatCurrency(v.totalCost)}</td>}
                    {isAdminView && <td>{formatCostPerKm(v.costPerKm)}</td>}
                    <td>
                      {isAdminView ? (
                        <select
                          className={`fmct-vehicle-status-select fmct-vehicle-status-select-${statusValue}`}
                          value={statusValue}
                          onChange={(e) => updateVehicleListMeta(v, { status: e.target.value })}
                          aria-label={`Status for ${v.plate}`}
                        >
                          {VEHICLE_STATUS_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className={`fmct-status fmct-status-${statusValue}`}>
                          {statusLabel(statusValue)}
                        </span>
                      )}
                    </td>
                    {isAdminView && (
                      <td className="fmct-vehicle-remark-cell">
                        <input
                          type="text"
                          className="fmct-vehicle-remark-input"
                          value={v.remark || ''}
                          placeholder="Add remark…"
                          onChange={(e) => updateVehicleListMeta(v, { remark: e.target.value })}
                          aria-label={`Remark for ${v.plate}`}
                        />
                      </td>
                    )}
                    <td>
                      {isAdminView && (
                        <button type="button" className="fmct-btn fmct-btn-sm" onClick={() => openVehicleDrawer(v)}>
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <div className="fmct-pagination">
        <span>{filteredVehicles.length} vehicle(s)</span>
        <div>
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span>Page {page} / {totalPages}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </div>
    </div>
  );

  const renderMaintenanceTable = () => (
    <div className="fmct-card fmct-table-card">
      <div className="fmct-table-toolbar">
        <h3>Maintenance History</h3>
        {isAdminView && (
          <button type="button" className="fmct-btn fmct-btn-outline" onClick={exportMaintenanceExcel}>
            <FileSpreadsheet className="h-4 w-4" /> Export Excel
          </button>
        )}
      </div>
      <div className="fmct-table-wrap">
        <table className="fmct-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Plate</th>
              <th>Item</th>
              {isAdminView && <th>Supplier</th>}
              {isAdminView && <th>Cost</th>}
              {isAdminView && <th>Invoice Number</th>}
              <th>Attachment</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredMaintenance.map((r) => (
              <tr key={r.id}>
                <td>{r.date}</td>
                <td>{r.plate}</td>
                <td>{r.item}</td>
                {isAdminView && <td>{r.supplier}</td>}
                {isAdminView && <td>{formatCurrency(r.cost)}</td>}
                {isAdminView && <td>{r.invoiceNumber}</td>}
                <td>{r.attachment ? <span className="fmct-attach">{r.attachment}</span> : '—'}</td>
                <td>
                  <span className={`fmct-status fmct-status-${r.status}`}>{statusLabel(r.status)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDriverPanelHeader = (v) => (
    <header className="fmct-driver-panel-header">
      <div>
        <h2>{v.plate}</h2>
        <p>{v.model}</p>
      </div>
      <span className={`fmct-status fmct-status-${v.displayStatus}`}>
        {statusLabel(v.displayStatus)}
      </span>
    </header>
  );

  const renderDriverPageShell = (children) => {
    if (!selectedDriverVehicle) return null;
    return (
      <div className="fmct-driver-dashboard">
        <article className="fmct-driver-panel">
          {renderDriverPanelHeader(selectedDriverVehicle)}
          <div className="fmct-driver-panel-content">{children}</div>
        </article>
      </div>
    );
  };

  const renderDriverProfileSection = (vehicle) => {
    const vehicleId = vehicle.id;
    const profile = getDriverProfileWithRental(vehicle);
    if (!profile) return null;

    const draft = driverProfileDraft || {};
    const avatarSrc = resolveLicenseImageSrc(
      isDriverProfileEditing ? draft.driverAvatar : profile.driverAvatar
    );
    const hasRentalSync = profile.rentalSource === 'car-rental-invoicing';
    const vehicleHistory = profile.vehicleHistory || [];
    const showVehicleHistory = vehicleHistory.length > 0;

    const updateProfileDraft = (field, value) => {
      setDriverProfileDraft((prev) => ({ ...(prev || {}), [field]: value }));
    };

    return (
      <section className="fmct-driver-panel-section fmct-driver-profile-section">
        <div className="fmct-driver-profile-toolbar">
          <div>
            <h4>Driver Information</h4>
            {hasRentalSync && (
              <p className="fmct-rental-sync-note">
                Synced from Car Rental Invoicing across {profile.rentalPeriodCount} month
                {profile.rentalPeriodCount === 1 ? '' : 's'}
                {profile.rentalMonth ? ` · latest: ${profile.rentalMonth}` : ''}
              </p>
            )}
            {hasRentalSync && profile.switchedVehicle && (
              <p className="fmct-rental-switch-note">
                Latest assignment is {profile.latestAssignmentReg} — different from this fleet vehicle ({vehicle.plate})
              </p>
            )}
            {carRentalsLoading && !hasRentalSync && (
              <p className="fmct-rental-sync-note">Loading rental assignments…</p>
            )}
          </div>
          {!isDriverProfileEditing ? (
            <button
              type="button"
              className="fmct-btn fmct-btn-sm fmct-btn-outline"
              onClick={() => startDriverProfileEdit(profile)}
            >
              <Pencil className="h-4 w-4" /> Edit profile
            </button>
          ) : (
            <div className="fmct-license-edit-actions">
              <button
                type="button"
                className="fmct-btn fmct-btn-sm fmct-btn-primary"
                disabled={driverProfileSaving}
                onClick={() =>
                  saveDriverProfileEdit(vehicleId, {
                    plate: vehicle.plate,
                    ownerRentalCompany: vehicle.ownerRentalCompany,
                    rentalCompany: profile.rentalCompany
                  })
                }
              >
                <Save className="h-4 w-4" /> {driverProfileSaving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="fmct-btn fmct-btn-sm fmct-btn-ghost"
                disabled={driverProfileSaving}
                onClick={cancelDriverProfileEdit}
              >
                <X className="h-4 w-4" /> Cancel
              </button>
            </div>
          )}
        </div>

        <div className="fmct-driver-profile-body">
          <div className="fmct-driver-profile-fields">
            {isDriverProfileEditing ? (
              <div className="fmct-license-form">
                {!hasRentalSync && (
                  <>
                    <label>Name<input value={draft.driver || ''} onChange={(e) => updateProfileDraft('driver', e.target.value)} /></label>
                    <label>Project<input value={draft.project || ''} onChange={(e) => updateProfileDraft('project', e.target.value)} /></label>
                  </>
                )}
                <label>Phone<input value={draft.phone || ''} onChange={(e) => updateProfileDraft('phone', e.target.value)} /></label>
                <label>Email<input type="email" value={draft.email || ''} onChange={(e) => updateProfileDraft('email', e.target.value)} /></label>
              </div>
            ) : (
              <dl className="fmct-dl">
                <dt>User name</dt><dd>{profile.driver}</dd>
                <dt>Project</dt><dd className="fmct-drawer-project">{profile.project}</dd>
                <dt>Reg number</dt><dd>{profile.regNumber || vehicle.plate}</dd>
                <dt>Rental company</dt><dd>{profile.rentalCompany || vehicle.ownerRentalCompany}</dd>
                <dt>Vehicles used</dt>
                <dd>{vehicleHistory.length || 1}</dd>
                <dt>Phone</dt><dd>{profile.phone}</dd>
                <dt>Email</dt><dd>{profile.email}</dd>
              </dl>
            )}
          </div>

          <div className="fmct-driver-avatar-wrap">
            {isDriverProfileEditing ? (
              <label className="fmct-driver-avatar-label">
                <input
                  type="file"
                  accept="image/*"
                  className="fmct-license-upload-input"
                  onChange={handleDriverAvatarUpload}
                />
                <img
                  src={avatarSrc}
                  alt={`${profile.driver} profile`}
                  className="fmct-driver-avatar"
                />
                <span className="fmct-driver-avatar-hint">
                  {draft.driverAvatarName ? draft.driverAvatarName : 'Tap to update photo'}
                </span>
              </label>
            ) : (
              <img
                src={avatarSrc}
                alt={`${profile.driver} profile`}
                className="fmct-driver-avatar"
              />
            )}
          </div>
        </div>

        {showVehicleHistory && !isDriverProfileEditing && (
          <div className="fmct-driver-vehicle-history">
            <h5>Vehicles used</h5>
            <p className="fmct-toolbar-sub">Assignment history from Car Rental Invoicing by month</p>
            <div className="fmct-table-wrap">
              <table className="fmct-table fmct-driver-history-table">
                <thead>
                  <tr>
                    <th>Reg number</th>
                    <th>Project</th>
                    <th>Rental company</th>
                    <th>Months used</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicleHistory.map((entry) => (
                    <tr
                      key={entry.regNumber}
                      className={
                        normalizeRegNumber(entry.regNumber) === normalizeRegNumber(vehicle.plate)
                          ? 'fmct-driver-history-row--current'
                          : ''
                      }
                    >
                      <td>{entry.regNumber}</td>
                      <td>{entry.project || '—'}</td>
                      <td>{entry.rentalCompany || '—'}</td>
                      <td>
                        <div className="fmct-month-chips">
                          {entry.months.map((m) => (
                            <span key={m.key} className="fmct-month-chip" title={m.project}>
                              {m.label}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    );
  };

  const renderDriverVehiclePanel = (v) => {
    const rentalProfile = getDriverProfileWithRental(v);
    const fleetDataId = resolveFleetDataVehicleId(v, vehicles);
    const records = fleetDataId ? getRecordsForVehicle(fleetDataId) : [];
    return (
      <article key={v.id} className="fmct-driver-panel">
        {renderDriverPanelHeader(v)}
        <div className="fmct-driver-panel-info-grid">
          <section className="fmct-driver-panel-section">
            <h4>Vehicle Information</h4>
            {rentalProfile?.vehicleRentalMonth && (
              <p className="fmct-rental-sync-note">
                Rental data for this plate · {rentalProfile.vehicleRentalMonth}
              </p>
            )}
            <dl className="fmct-dl">
              <dt>Plate</dt><dd>{rentalProfile?.vehicleRegNumber || v.plate}</dd>
              <dt>Model</dt><dd>{v.model}</dd>
              <dt>Project</dt><dd className="fmct-drawer-project">{rentalProfile?.vehicleProject || v.project}</dd>
              <dt>Owner / Rental company</dt><dd>{rentalProfile?.vehicleRentalCompany || v.ownerRentalCompany}</dd>
              <dt>Odometer</dt><dd>{formatKm(v.odometer)}</dd>
              <dt>Status</dt>
              <dd>
                <span className={`fmct-status fmct-status-${v.displayStatus}`}>
                  {statusLabel(v.displayStatus)}
                </span>
              </dd>
            </dl>
          </section>

          {renderDriverProfileSection(v)}
        </div>

        <section className="fmct-driver-panel-section fmct-driver-panel-section--last">
          <h4>Maintenance History</h4>
          <div className="fmct-driver-panel-table-wrap">
            <table className="fmct-table fmct-table-compact">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Item</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id}>
                    <td>{r.date}</td>
                    <td>{r.item}</td>
                    <td>{statusLabel(r.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </article>
    );
  };

  const renderDriverDashboard = () => {
    if (!selectedDriverVehicle) return null;
    return (
      <div className="fmct-driver-dashboard">
        {renderDriverVehiclePanel(selectedDriverVehicle)}
      </div>
    );
  };

  const renderTopnavProjectFilter = () => (
    <div className="fmct-topnav-project-filter">
      <label htmlFor="topnav-project-filter" className="fmct-topnav-project-label">
        Project
      </label>
      <select
        id="topnav-project-filter"
        className="fmct-topnav-project-select"
        value={projectFilter}
        onChange={(e) => {
          setProjectFilter(e.target.value);
          setPage(1);
        }}
        aria-label="Filter by project"
      >
        {topnavProjectOptions.map((p) => (
          <option key={p} value={p}>
            {p === 'all' ? 'All projects' : p}
          </option>
        ))}
      </select>
    </div>
  );

  const renderTopnavVehicleSelect = () => (
    <div className="fmct-choose-vehicle fmct-vehicle-picker" ref={vehiclePickerRef}>
      <label htmlFor="topnav-vehicle-search" className="fmct-choose-vehicle-label">
        Choose vehicle
      </label>
      <div className={`fmct-vehicle-picker-control${vehiclePickerOpen ? ' is-open' : ''}`}>
        <Search className="fmct-vehicle-picker-icon" aria-hidden />
        <input
          id="topnav-vehicle-search"
          type="search"
          className="fmct-vehicle-picker-input"
          placeholder={
            visibleDriverVehicles.length
              ? 'Search plate, driver, project…'
              : 'No vehicles for this project'
          }
          value={
            vehiclePickerOpen
              ? vehicleSearchQuery
              : formatDriverVehicleLabel(selectedDriverVehicle)
          }
          onChange={(e) => {
            setVehicleSearchQuery(e.target.value);
            if (!vehiclePickerOpen) setVehiclePickerOpen(true);
          }}
          onFocus={() => {
            setVehiclePickerOpen(true);
            setVehicleSearchQuery('');
          }}
          disabled={visibleDriverVehicles.length === 0}
          autoComplete="off"
          aria-label="Choose vehicle"
          aria-expanded={vehiclePickerOpen}
          aria-controls="topnav-vehicle-listbox"
          role="combobox"
        />
        {vehiclePickerOpen && visibleDriverVehicles.length > 0 && (
          <ul id="topnav-vehicle-listbox" className="fmct-vehicle-picker-list" role="listbox">
            {filteredVehiclePickerOptions.length === 0 ? (
              <li className="fmct-vehicle-picker-empty">No matches</li>
            ) : (
              filteredVehiclePickerOptions.map((v) => (
                <li key={v.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selectedDriverVehicle?.id === v.id}
                    className={selectedDriverVehicle?.id === v.id ? 'is-selected' : ''}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setDriverVehicleId(v.id);
                      setVehiclePickerOpen(false);
                      setVehicleSearchQuery('');
                    }}
                  >
                    {formatDriverVehicleLabel(v)}
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </div>
  );

  const renderDriverMaintenance = () => {
    if (!selectedDriverVehicle) return null;
    const v = selectedDriverVehicle;
    const reportKey = getVehicleListKey(v);
    const reports = [...(maintenanceReports[reportKey] || [])].sort((a, b) =>
      String(b.date || '').localeCompare(String(a.date || ''))
    );

    // Suggest standard items + every issue ever reported (all vehicles) so
    // previously-entered issues are kept and easy to reuse.
    const issueHistory = Array.from(
      new Set(
        Object.values(maintenanceReports)
          .flat()
          .map((r) => String(r?.item || '').trim())
          .filter(Boolean)
      )
    );
    const issueSuggestions = Array.from(new Set([...issueHistory, ...MAINTENANCE_ITEMS]));

    const openMaintenanceDialog = () => {
      setMaintenanceRequest({
        date: new Date().toISOString().slice(0, 10),
        item: '',
        odometer: String(v.odometer || ''),
        location: '',
        channel: 'WhatsApp',
        status: 'reported',
        reportedBy: '',
        remark: '',
        error: ''
      });
      setMaintenanceDialogOpen(true);
    };

    const closeMaintenanceDialog = () => setMaintenanceDialogOpen(false);

    const submitMaintenanceReport = (e) => {
      e.preventDefault();
      if (!maintenanceRequest.item.trim()) {
        setMaintenanceRequest((s) => ({ ...s, error: 'Please enter the issue.' }));
        return;
      }
      const record = {
        id: `mr-${reportKey}-${Date.now()}`,
        date: maintenanceRequest.date || new Date().toISOString().slice(0, 10),
        item: maintenanceRequest.item,
        odometer: Number(maintenanceRequest.odometer) || 0,
        location: (maintenanceRequest.location || '').trim(),
        channel: maintenanceRequest.channel || 'WhatsApp',
        status: maintenanceRequest.status || 'reported',
        reportedBy: (maintenanceRequest.reportedBy || v.driver || '').trim() || v.driver || '—',
        remark: (maintenanceRequest.remark || '').trim()
      };
      setMaintenanceReports((prev) => ({
        ...prev,
        [reportKey]: [...(prev[reportKey] || []), record]
      }));
      closeMaintenanceDialog();
    };

    const removeReport = (id) => {
      setMaintenanceReports((prev) => ({
        ...prev,
        [reportKey]: (prev[reportKey] || []).filter((r) => r.id !== id)
      }));
    };

    return renderDriverPageShell(
      <div className="fmct-driver-action-page">
        <div className="fmct-card fmct-table-card">
          <div className="fmct-table-toolbar">
            <div>
              <h3>Maintenance</h3>
              <p className="fmct-toolbar-sub">Maintenance reports for {v.plate}</p>
            </div>
            <button type="button" className="fmct-btn fmct-btn-primary" onClick={openMaintenanceDialog}>
              <Plus className="h-4 w-4" /> Report Maintenance
            </button>
          </div>
          <div className="fmct-table-wrap">
            <table className="fmct-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Issue</th>
                  <th>Odometer</th>
                  <th>Location</th>
                  <th>Reported (WhatsApp/Email)</th>
                  <th>Status</th>
                  <th>Reported by</th>
                  <th>Remark</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {reports.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="fmct-table-empty">
                      No maintenance reports yet.
                    </td>
                  </tr>
                ) : (
                  reports.map((r) => (
                    <tr key={r.id}>
                      <td>{r.date}</td>
                      <td>{r.item}</td>
                      <td>{r.odometer ? formatKm(r.odometer) : '—'}</td>
                      <td>{r.location || '—'}</td>
                      <td>{r.channel}</td>
                      <td>
                        <span className={`fmct-status fmct-status-${r.status}`}>
                          {statusLabel(r.status)}
                        </span>
                      </td>
                      <td>{r.reportedBy}</td>
                      <td>{r.remark || '—'}</td>
                      <td>
                        <button
                          type="button"
                          className="fmct-btn fmct-btn-sm fmct-btn-ghost"
                          onClick={() => removeReport(r.id)}
                          aria-label="Delete report"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {maintenanceDialogOpen && (
          <div className="fmct-modal-overlay" onClick={closeMaintenanceDialog} role="presentation">
            <div
              className="fmct-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-label="Report maintenance"
            >
              <div className="fmct-modal-header">
                <div>
                  <h3>Report Maintenance</h3>
                  <p>
                    {v.plate} · {v.model === 'Rental vehicle' ? v.project : v.model} · {v.driver}
                  </p>
                </div>
                <button type="button" className="fmct-modal-close" onClick={closeMaintenanceDialog} aria-label="Close">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="fmct-modal-body">
                <form className="fmct-form-grid" onSubmit={submitMaintenanceReport}>
                  <label>
                    Date
                    <input
                      type="date"
                      value={maintenanceRequest.date}
                      onChange={(e) => setMaintenanceRequest((s) => ({ ...s, date: e.target.value }))}
                    />
                  </label>
                  <label>
                    Issue
                    <input
                      type="text"
                      list="fmct-maintenance-issue-options"
                      placeholder="Describe the issue…"
                      value={maintenanceRequest.item}
                      onChange={(e) => setMaintenanceRequest((s) => ({ ...s, item: e.target.value, error: '' }))}
                    />
                    <datalist id="fmct-maintenance-issue-options">
                      {issueSuggestions.map((item) => (
                        <option key={item} value={item} />
                      ))}
                    </datalist>
                  </label>
                  <label>
                    Odometer (KM)
                    <input
                      type="number"
                      min="0"
                      placeholder={String(v.odometer || 0)}
                      value={maintenanceRequest.odometer}
                      onChange={(e) => setMaintenanceRequest((s) => ({ ...s, odometer: e.target.value }))}
                    />
                  </label>
                  <label className="fmct-form-full">
                    Location
                    <input
                      type="text"
                      placeholder="Where is the vehicle / issue?"
                      value={maintenanceRequest.location}
                      onChange={(e) => setMaintenanceRequest((s) => ({ ...s, location: e.target.value }))}
                    />
                  </label>
                  <label>
                    Reported (WhatsApp / Email)
                    <select
                      value={maintenanceRequest.channel}
                      onChange={(e) => setMaintenanceRequest((s) => ({ ...s, channel: e.target.value }))}
                    >
                      {MAINTENANCE_CHANNEL_OPTIONS.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Status
                    <select
                      value={maintenanceRequest.status}
                      onChange={(e) => setMaintenanceRequest((s) => ({ ...s, status: e.target.value }))}
                    >
                      {MAINTENANCE_REPORT_STATUS_OPTIONS.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Reported by
                    <input
                      type="text"
                      placeholder={v.driver || 'Name'}
                      value={maintenanceRequest.reportedBy}
                      onChange={(e) => setMaintenanceRequest((s) => ({ ...s, reportedBy: e.target.value }))}
                    />
                  </label>
                  <label className="fmct-form-full">
                    Remark
                    <textarea
                      rows={2}
                      placeholder="Add a remark…"
                      value={maintenanceRequest.remark}
                      onChange={(e) => setMaintenanceRequest((s) => ({ ...s, remark: e.target.value }))}
                    />
                  </label>
                  {maintenanceRequest.error && (
                    <div className="fmct-form-error fmct-form-full">{maintenanceRequest.error}</div>
                  )}
                  <div className="fmct-form-actions fmct-form-full">
                    <button type="button" className="fmct-btn fmct-btn-outline" onClick={closeMaintenanceDialog}>
                      Cancel
                    </button>
                    <button type="submit" className="fmct-btn fmct-btn-primary">
                      <Plus className="h-4 w-4" /> Add Report
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDriverBreakdown = () => {
    if (!selectedDriverVehicle) return null;
    const v = selectedDriverVehicle;
    const fleetDataId = resolveFleetDataVehicleId(v, vehicles);
    const mergedBreakdownRecords = [
      ...(fleetDataId
        ? getBreakdownRecordsForVehicle(fleetDataId).map((r) => ({
            ...r,
            vehicleId: v.id
          }))
        : []),
      ...(breakdownRecordOverrides[v.id] || [])
    ].sort((a, b) => b.date.localeCompare(a.date));

    const openBreakdownDialog = () => {
      setBreakdownForm({
        date: new Date().toISOString().slice(0, 10),
        location: '',
        odometer: String(v.odometer || ''),
        issue: '',
        attachment: '',
        attachmentName: ''
      });
      setBreakdownFormError('');
      setBreakdownDialogOpen(true);
    };

    const closeBreakdownDialog = () => {
      setBreakdownDialogOpen(false);
      setBreakdownFormError('');
    };

    const submitBreakdownReport = (e) => {
      e.preventDefault();
      if (!breakdownForm.location.trim()) {
        setBreakdownFormError('Please enter the breakdown location.');
        return;
      }
      if (!breakdownForm.issue.trim()) {
        setBreakdownFormError('Please describe the issue.');
        return;
      }
      setBreakdownFormError('');
      const record = {
        id: `bd-${v.id}-${Date.now()}`,
        vehicleId: v.id,
        date: breakdownForm.date,
        location: breakdownForm.location.trim(),
        odometer: Number(breakdownForm.odometer) || 0,
        issue: breakdownForm.issue.trim(),
        status: 'reported',
        reportedBy: v.driver,
        attachmentPhoto: breakdownForm.attachment || null,
        attachmentPhotoName: breakdownForm.attachmentName || ''
      };
      setBreakdownRecordOverrides((prev) => ({
        ...prev,
        [v.id]: [...(prev[v.id] || []), record]
      }));
      closeBreakdownDialog();
    };

    const openBreakdownPhoto = (photo) => {
      const win = window.open('', '_blank');
      if (!win) return;
      win.document.write(
        `<html><head><title>Breakdown photo</title></head><body style="margin:0;background:#111;display:flex;align-items:center;justify-content:center;min-height:100vh"><img src="${photo}" alt="Breakdown" style="max-width:100%;max-height:100vh" /></body></html>`
      );
      win.document.close();
    };

    return renderDriverPageShell(
      <div className="fmct-driver-action-page">
        <div className="fmct-card fmct-table-card">
          <div className="fmct-table-toolbar">
            <div>
              <h3>Breakdown</h3>
              <p className="fmct-toolbar-sub">Vehicle breakdown reports for {v.plate}</p>
            </div>
            <button type="button" className="fmct-btn fmct-btn-primary" onClick={openBreakdownDialog}>
              <AlertTriangle className="h-4 w-4" /> Report Breakdown
            </button>
          </div>
          <div className="fmct-table-wrap">
            <table className="fmct-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Location</th>
                  <th>Odometer</th>
                  <th>Issue</th>
                  <th>Status</th>
                  <th>Photo</th>
                </tr>
              </thead>
              <tbody>
                {mergedBreakdownRecords.length === 0 ? (
                  <tr>
                    <td colSpan={6}>No breakdown reports on file.</td>
                  </tr>
                ) : (
                  mergedBreakdownRecords.map((r) => (
                    <tr key={r.id}>
                      <td>{formatLicenseDisplayDate(r.date)}</td>
                      <td>{r.location}</td>
                      <td>{formatKm(r.odometer)}</td>
                      <td>{r.issue}</td>
                      <td>
                        <span className={`fmct-status fmct-status-${getBreakdownStatusClass(r.status)}`}>
                          {getBreakdownStatusLabel(r.status)}
                        </span>
                      </td>
                      <td>
                        {r.attachmentPhoto ? (
                          <button
                            type="button"
                            className="fmct-fuel-receipt-btn"
                            onClick={() => openBreakdownPhoto(r.attachmentPhoto)}
                          >
                            View photo
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {breakdownDialogOpen && (
          <div className="fmct-modal-overlay" onClick={closeBreakdownDialog} role="presentation">
            <div
              className="fmct-modal fmct-breakdown-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-label="Report breakdown"
            >
              <div className="fmct-modal-header">
                <div>
                  <h3>Report Breakdown</h3>
                  <p>
                    {v.plate} · {v.model === 'Rental vehicle' ? v.project : v.model} · {v.driver}
                  </p>
                </div>
                <button type="button" className="fmct-modal-close" onClick={closeBreakdownDialog} aria-label="Close">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="fmct-modal-body">
                <form className="fmct-form-grid fmct-breakdown-form" onSubmit={submitBreakdownReport}>
                  <label>
                    Date
                    <input
                      type="date"
                      required
                      value={breakdownForm.date}
                      onChange={(e) => setBreakdownForm((s) => ({ ...s, date: e.target.value }))}
                    />
                  </label>
                  <label>
                    Odometer (KM)
                    <input
                      type="number"
                      min="0"
                      required
                      placeholder={String(v.odometer || 0)}
                      value={breakdownForm.odometer}
                      onChange={(e) => setBreakdownForm((s) => ({ ...s, odometer: e.target.value }))}
                    />
                  </label>
                  <label className="fmct-form-full">
                    Location
                    <input
                      type="text"
                      required
                      placeholder="Where did the breakdown occur?"
                      value={breakdownForm.location}
                      onChange={(e) => setBreakdownForm((s) => ({ ...s, location: e.target.value }))}
                    />
                  </label>
                  <label className="fmct-form-full">
                    Issue description
                    <textarea
                      rows={3}
                      required
                      placeholder="Describe what happened…"
                      value={breakdownForm.issue}
                      onChange={(e) => setBreakdownForm((s) => ({ ...s, issue: e.target.value }))}
                    />
                  </label>
                  <label className="fmct-form-full">
                    Photo (optional)
                    <input
                      type="file"
                      accept="image/*"
                      className="fmct-license-upload-input"
                      onChange={handleBreakdownAttachmentUpload}
                    />
                    {breakdownForm.attachment ? (
                      <img
                        src={breakdownForm.attachment}
                        alt="Breakdown preview"
                        className="fmct-breakdown-photo-preview"
                      />
                    ) : (
                      <span className="fmct-toolbar-sub">Attach a photo of the breakdown if available</span>
                    )}
                  </label>
                  {breakdownFormError && <p className="fmct-form-error fmct-form-full">{breakdownFormError}</p>}
                  <div className="fmct-form-actions fmct-form-full">
                    <button type="button" className="fmct-btn fmct-btn-outline" onClick={closeBreakdownDialog}>
                      Cancel
                    </button>
                    <button type="submit" className="fmct-btn fmct-btn-primary">
                      <Plus className="h-4 w-4" /> Submit Report
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDriverFuel = () => {
    if (!selectedDriverVehicle) return null;
    // eslint-disable-next-line no-unused-vars
    const fleetDataId = resolveFleetDataVehicleId(selectedDriverVehicle, vehicles);
    // Backend FuelLogsSGM only — mock FUEL_RECORDS are not shown in driver Fuel History.
    const fuelPool = [
      ...(fuelLogsFromDb || []).map((r) => ({
        ...r,
        vehicleId: selectedDriverVehicle.id
      })),
      ...(fuelRecordOverrides[selectedDriverVehicle.id] || [])
    ];
    const mergedFuelRecords = enrichFuelRecordsForVehicle(
      selectedDriverVehicle.id,
      fuelPool
    ).filter((r) => {
      if (driverMonthFilter === 'all') return true;
      const date = String(r.fillDate || r.date || '').trim();
      return date.startsWith(driverMonthFilter);
    });

    const formatFuelCardOrCash = (record) => {
      if (record.cardUsed && record.fuelCardNumber) return record.fuelCardNumber;
      if (record.cardUsed) return 'Card';
      return 'Cash';
    };

    const getFuelDistanceKm = (record) => {
      const previous = Number(record.previousOdometer);
      const current = Number(record.odometer);
      if (!Number.isFinite(previous) || !Number.isFinite(current)) return null;
      const distanceKm = current - previous;
      if (!Number.isFinite(distanceKm) || distanceKm < 0) return null;
      return distanceKm;
    };

    const formatFuelKmTraveled = (record) => {
      const distanceKm = getFuelDistanceKm(record);
      if (distanceKm == null) return '—';
      return formatKm(distanceKm);
    };

    const formatFuelConsumption = (record) => {
      const distanceKm = getFuelDistanceKm(record);
      const liters = Number(record.liters);
      if (distanceKm == null || !Number.isFinite(liters) || liters <= 0) return '—';
      const kmPerLiter = distanceKm / liters;
      return `${kmPerLiter.toLocaleString('en-US', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2
      })} Km/L`;
    };

    const activeFuelVehicles = visibleDriverVehicles.filter((v) => !isVehicleInactive(v));
    const canLogFuel = activeFuelVehicles.length > 0;
    const fuelTargetVehicle =
      activeFuelVehicles.find((v) => v.id === fuelForm.vehicleId) || null;
    const fuelVehicleSelected = Boolean(fuelTargetVehicle);

    const openFuelDialog = () => {
      if (!canLogFuel) return;
      setFuelForm({
        vehicleId: '',
        date: new Date().toISOString().slice(0, 10),
        previousOdometer: '',
        odometer: '',
        liters: '',
        fuelCost: '',
        paymentType: 'card',
        fuelCardNumber: '',
        cashAttachment: '',
        cashAttachmentName: ''
      });
      setFuelFormError('');
      setFuelDialogOpen(true);
    };

    const closeFuelDialog = () => {
      setFuelDialogOpen(false);
      setFuelFormError('');
    };

    const handleFuelVehicleChange = (vehicleId) => {
      const vehicle = activeFuelVehicles.find((v) => v.id === vehicleId) || null;
      if (!vehicle) {
        setFuelForm((s) => ({
          ...s,
          vehicleId: '',
          previousOdometer: '',
          odometer: '',
          liters: '',
          fuelCost: '',
          paymentType: 'card',
          fuelCardNumber: '',
          cashAttachment: '',
          cashAttachmentName: ''
        }));
        setFuelFormError('');
        return;
      }
      // Keep KM fields empty for every new log — driver must enter them.
      setFuelForm((s) => ({
        ...s,
        vehicleId: vehicle.id,
        previousOdometer: '',
        odometer: '',
        liters: '',
        fuelCost: '',
        paymentType: 'card',
        fuelCardNumber: '',
        cashAttachment: '',
        cashAttachmentName: ''
      }));
      setFuelFormError('');
    };

    const submitFuelEntry = async (e) => {
      e.preventDefault();
      if (fuelSaving) return;
      if (!fuelTargetVehicle || isVehicleInactive(fuelTargetVehicle)) {
        setFuelFormError('Please select an active vehicle first.');
        return;
      }
      if (!String(fuelForm.date || '').trim()) {
        setFuelFormError('Please enter the date of fill.');
        return;
      }
      const previousOdometer = Number(String(fuelForm.previousOdometer).replace(/,/g, '').trim());
      const odometer = Number(String(fuelForm.odometer).replace(/,/g, '').trim());
      const liters = Number(String(fuelForm.liters).replace(/,/g, '').trim());
      const fuelCost = Number(String(fuelForm.fuelCost).replace(/,/g, '').trim());

      if (fuelForm.previousOdometer === '' || !Number.isFinite(previousOdometer) || previousOdometer < 0) {
        setFuelFormError('Please enter the previous odometer (KM).');
        return;
      }
      if (fuelForm.odometer === '' || !Number.isFinite(odometer) || odometer <= 0) {
        setFuelFormError('Please enter the current odometer (KM).');
        return;
      }
      if (odometer < previousOdometer) {
        setFuelFormError('Current odometer must be greater than or equal to previous odometer.');
        return;
      }
      if (fuelForm.liters === '' || !Number.isFinite(liters) || liters <= 0) {
        setFuelFormError('Please enter the fuel quantity (L).');
        return;
      }
      if (fuelForm.fuelCost === '' || !Number.isFinite(fuelCost) || fuelCost < 0) {
        setFuelFormError('Please enter the fuel cost (TZS).');
        return;
      }
      if (!fuelForm.paymentType) {
        setFuelFormError('Please select a payment type.');
        return;
      }

      const cardUsed = fuelForm.paymentType === 'card';
      if (cardUsed && !fuelForm.fuelCardNumber.trim()) {
        setFuelFormError('Please enter the fuel card number.');
        return;
      }
      if (!cardUsed && !fuelForm.cashAttachment) {
        setFuelFormError('Please attach a receipt photo for cash payments.');
        return;
      }
      setFuelFormError('');
      const targetFleetId = resolveFleetDataVehicleId(fuelTargetVehicle, vehicles);
      const serviceVehicleId = targetFleetId || fuelTargetVehicle.id;

      setFuelSaving(true);
      try {
        const saved = await createFuelLog({
          vehicleId: serviceVehicleId,
          plate: fuelTargetVehicle.plate,
          driver: fuelTargetVehicle.driver,
          project: fuelTargetVehicle.project,
          fillDate: fuelForm.date,
          previousOdometer,
          odometer,
          liters,
          fuelCost,
          paymentType: cardUsed ? 'card' : 'cash',
          cardUsed,
          fuelCardNumber: cardUsed ? fuelForm.fuelCardNumber.trim() : '',
          station: '',
          attachmentPhoto: cardUsed ? null : fuelForm.cashAttachment,
          attachmentPhotoName: cardUsed ? '' : fuelForm.cashAttachmentName,
          submittedByEmail: authSession?.email || '',
          submittedByName: authSession?.name || ''
        });
        const normalized = { ...saved, vehicleId: fuelTargetVehicle.id };
        if (fuelTargetVehicle.id === selectedDriverVehicle.id) {
          setFuelLogsFromDb((prev) =>
            [normalized, ...(prev || [])].sort((a, b) =>
              String(b.date).localeCompare(String(a.date))
            )
          );
        } else {
          setDriverVehicleId(fuelTargetVehicle.id);
        }
        closeFuelDialog();
      } catch (err) {
        const localRecord = {
          id: `fuel-${fuelTargetVehicle.id}-${Date.now()}`,
          vehicleId: fuelTargetVehicle.id,
          plate: fuelTargetVehicle.plate,
          date: fuelForm.date,
          liters,
          previousOdometer,
          odometer,
          fuelCost,
          fuelCardNumber: cardUsed ? fuelForm.fuelCardNumber.trim() : '',
          cardUsed,
          station: '',
          attachmentPhoto: cardUsed ? null : fuelForm.cashAttachment,
          attachmentPhotoName: cardUsed ? '' : fuelForm.cashAttachmentName
        };
        setFuelRecordOverrides((prev) => ({
          ...prev,
          [fuelTargetVehicle.id]: [...(prev[fuelTargetVehicle.id] || []), localRecord]
        }));
        setFuelFormError(getFuelLogErrorMessage(err) || 'Failed to save fuel log.');
      } finally {
        setFuelSaving(false);
      }
    };

    return renderDriverPageShell(
      <div className="fmct-driver-action-page">
        {selectedVehicleInactive && (
          <div className="fmct-card fmct-inactive-vehicle-banner" role="status">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            <div>
              <strong>{selectedDriverVehicle.plate} is inactive</strong>
              <p>
                This vehicle cannot receive fuel entries. Use Log Fuel Entry and choose an active
                vehicle.
              </p>
            </div>
          </div>
        )}
        <div className="fmct-driver-fuel-summary">
          <div className="fmct-driver-fuel-summary-head">
            <div>
              <h3>Fuel summary</h3>
              <p className="fmct-card-sub">
                {selectedDriverVehicle.plate}
                {driverFuelSummary.fillCount
                  ? ` · ${driverFuelSummary.fillCount} fill-up${
                      driverFuelSummary.fillCount === 1 ? '' : 's'
                    }`
                  : ''}
              </p>
            </div>
            <div className="fmct-fuel-month-filter-wrap">
              <label className="fmct-fuel-month-filter" htmlFor="fuel-page-month-filter">
                <span>Month</span>
                <select
                  id="fuel-page-month-filter"
                  value={driverMonthFilter}
                  onChange={(e) => setDriverMonthFilter(e.target.value)}
                  aria-label="Filter by month"
                >
                  {driverMonthFilterOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              {driverMonthFilter !== 'all' && (
                <button
                  type="button"
                  className="fmct-btn fmct-btn-ghost fmct-fuel-month-clear"
                  onClick={() => setDriverMonthFilter('all')}
                >
                  Clear filter
                </button>
              )}
            </div>
          </div>
          <div className="fmct-driver-fuel-summary-grid">
            <div className="fmct-kpi-card">
              <div className="fmct-kpi-icon">
                <Fuel className="h-4 w-4" />
              </div>
              <div>
                <div className="fmct-kpi-value">
                  {Number(driverFuelSummary.liters).toLocaleString('en-US', {
                    maximumFractionDigits: 1
                  })}{' '}
                  L
                </div>
                <div className="fmct-kpi-label">Total fuel used</div>
              </div>
            </div>
            <div className="fmct-kpi-card">
              <div className="fmct-kpi-icon">
                <LineChartIcon className="h-4 w-4" />
              </div>
              <div>
                <div className="fmct-kpi-value">
                  {formatCurrency(driverFuelSummary.amount)}
                </div>
                <div className="fmct-kpi-label">Total fuel amount</div>
              </div>
            </div>
            <div className="fmct-kpi-card">
              <div className="fmct-kpi-icon">
                <Car className="h-4 w-4" />
              </div>
              <div>
                <div className="fmct-kpi-value">{formatKm(driverFuelSummary.km)}</div>
                <div className="fmct-kpi-label">Total KM moved</div>
              </div>
            </div>
            <div className="fmct-kpi-card">
              <div className="fmct-kpi-icon">
                <Gauge className="h-4 w-4" />
              </div>
              <div>
                <div className="fmct-kpi-value">
                  {driverFuelSummary.avgConsumption != null
                    ? `${driverFuelSummary.avgConsumption.toLocaleString('en-US', {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 2
                      })} Km/L`
                    : '—'}
                </div>
                <div className="fmct-kpi-label">Average consumption</div>
              </div>
            </div>
          </div>
        </div>
        <div className="fmct-card fmct-table-card">
          <div className="fmct-table-toolbar">
            <div>
              <h3>Fuel History</h3>
              <p className="fmct-toolbar-sub">
                {selectedDriverVehicle.plate} ·{' '}
                {driverMonthFilter === 'all'
                  ? 'All months'
                  : driverMonthFilterOptions.find((o) => o.value === driverMonthFilter)?.label ||
                    driverMonthFilter}
              </p>
            </div>
            <button
              type="button"
              className="fmct-btn fmct-btn-primary"
              onClick={openFuelDialog}
              disabled={!canLogFuel}
              title={!canLogFuel ? 'No active vehicle available for fuel logging' : undefined}
            >
              <Fuel className="h-4 w-4" /> Log Fuel Entry
            </button>
          </div>
          <div className="fmct-table-wrap">
            <table className="fmct-table">
              <thead>
                <tr>
                  <th>Date of fill</th>
                  <th>Previous odometer</th>
                  <th>Current odometer</th>
                  <th>Km Traveled</th>
                  <th>Fuel (L)</th>
                  <th>Consumption (Km/L)</th>
                  <th>Fuel cost</th>
                  <th>Fuel card number / cash</th>
                </tr>
              </thead>
              <tbody>
                {mergedFuelRecords.length === 0 ? (
                  <tr>
                    <td colSpan={8}>No fuel records on file.</td>
                  </tr>
                ) : (
                  mergedFuelRecords.map((r) => (
                    <tr key={r.id}>
                      <td>{formatLicenseDisplayDate(r.date)}</td>
                      <td>{formatKm(r.previousOdometer)}</td>
                      <td>{formatKm(r.odometer)}</td>
                      <td>{formatFuelKmTraveled(r)}</td>
                      <td>{Number(r.liters).toLocaleString('en-US')} L</td>
                      <td>{formatFuelConsumption(r)}</td>
                      <td>{formatCurrency(r.fuelCost)}</td>
                      <td>
                        <span>{formatFuelCardOrCash(r)}</span>
                        {r.attachmentPhoto && !r.cardUsed && (
                          <button
                            type="button"
                            className="fmct-fuel-receipt-btn"
                            onClick={() => {
                              const win = window.open('', '_blank');
                              if (!win) return;
                              win.document.write(
                                `<html><head><title>Cash receipt</title></head><body style="margin:0;background:#111;display:flex;align-items:center;justify-content:center;min-height:100vh"><img src="${r.attachmentPhoto}" alt="Cash receipt" style="max-width:100%;max-height:100vh" /></body></html>`
                              );
                              win.document.close();
                            }}
                          >
                            View receipt
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {fuelDialogOpen && (
          <div className="fmct-modal-overlay" onClick={closeFuelDialog} role="presentation">
            <div
              className="fmct-modal fmct-fuel-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-label="Log fuel entry"
            >
              <div className="fmct-modal-header">
                <div>
                  <h3>Log Fuel Entry</h3>
                  <p>
                    {fuelTargetVehicle
                      ? `${fuelTargetVehicle.plate} · ${fuelTargetVehicle.model} · ${fuelTargetVehicle.driver}`
                      : 'Select a vehicle to continue'}
                  </p>
                </div>
                <button type="button" className="fmct-modal-close" onClick={closeFuelDialog} aria-label="Close">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="fmct-modal-body">
                <form className="fmct-form-grid fmct-fuel-form" onSubmit={submitFuelEntry}>
                  <label className="fmct-form-full">
                    Vehicle
                    <select
                      required
                      value={fuelForm.vehicleId}
                      onChange={(e) => handleFuelVehicleChange(e.target.value)}
                    >
                      <option value="">Select vehicle…</option>
                      {activeFuelVehicles.map((v) => (
                        <option key={v.id} value={v.id}>
                          {formatDriverVehicleLabel(v)}
                        </option>
                      ))}
                    </select>
                  </label>

                  {!fuelVehicleSelected ? (
                    <p className="fmct-form-hint fmct-form-full">
                      Choose a vehicle first. The remaining fuel fields will appear after selection.
                    </p>
                  ) : (
                    <>
                      <label>
                        Date of fill
                        <input
                          type="date"
                          required
                          value={fuelForm.date}
                          onChange={(e) => setFuelForm((s) => ({ ...s, date: e.target.value }))}
                        />
                      </label>
                      <label>
                        Previous odometer (KM)
                        <input
                          type="number"
                          min="0"
                          required
                          placeholder="Enter previous KM"
                          value={fuelForm.previousOdometer}
                          onChange={(e) =>
                            setFuelForm((s) => ({ ...s, previousOdometer: e.target.value }))
                          }
                        />
                      </label>
                      <label>
                        Current odometer (KM)
                        <input
                          type="number"
                          min="1"
                          required
                          placeholder="Enter current KM"
                          value={fuelForm.odometer}
                          onChange={(e) => setFuelForm((s) => ({ ...s, odometer: e.target.value }))}
                        />
                      </label>
                      <label>
                        Fuel (L)
                        <input
                          type="number"
                          min="1"
                          step="0.1"
                          required
                          placeholder="e.g. 60"
                          value={fuelForm.liters}
                          onChange={(e) => setFuelForm((s) => ({ ...s, liters: e.target.value }))}
                        />
                      </label>
                      <label>
                        Fuel cost (TZS)
                        <input
                          type="number"
                          min="0"
                          required
                          placeholder="e.g. 180000"
                          value={fuelForm.fuelCost}
                          onChange={(e) => setFuelForm((s) => ({ ...s, fuelCost: e.target.value }))}
                        />
                      </label>
                      <label>
                        Payment
                        <select
                          value={fuelForm.paymentType}
                          onChange={(e) => {
                            const paymentType = e.target.value;
                            setFuelForm((s) => ({
                              ...s,
                              paymentType,
                              ...(paymentType === 'card'
                                ? { cashAttachment: '', cashAttachmentName: '' }
                                : { fuelCardNumber: '' })
                            }));
                            setFuelFormError('');
                          }}
                        >
                          <option value="card">Fuel card</option>
                          <option value="cash">Cash</option>
                        </select>
                      </label>
                      {fuelForm.paymentType === 'card' && (
                        <label>
                          Fuel card number
                          <input
                            type="text"
                            required
                            placeholder="e.g. TE-88421"
                            value={fuelForm.fuelCardNumber}
                            onChange={(e) =>
                              setFuelForm((s) => ({ ...s, fuelCardNumber: e.target.value }))
                            }
                          />
                        </label>
                      )}
                      {fuelForm.paymentType === 'cash' && (
                        <div className="fmct-vir-attachment fmct-form-full fmct-fuel-cash-attachment">
                          <span className="fmct-vir-attachment-label">Receipt photo *</span>
                          <label className="fmct-vir-attachment-upload">
                            <input
                              type="file"
                              accept="image/*"
                              className="fmct-license-upload-input"
                              onChange={handleFuelAttachmentUpload}
                            />
                            {fuelForm.cashAttachment ? (
                              <img
                                src={fuelForm.cashAttachment}
                                alt="Cash receipt preview"
                                className="fmct-vir-attachment-preview"
                              />
                            ) : (
                              <div className="fmct-vir-attachment-placeholder">
                                <Upload className="h-4 w-4" />
                                <span>Tap to attach receipt photo</span>
                              </div>
                            )}
                            <span className="fmct-vir-attachment-hint">
                              {fuelForm.cashAttachmentName || 'Required for cash payments'}
                            </span>
                          </label>
                          {fuelForm.cashAttachment && (
                            <button
                              type="button"
                              className="fmct-btn fmct-btn-ghost fmct-vir-attachment-remove"
                              onClick={() =>
                                setFuelForm((s) => ({
                                  ...s,
                                  cashAttachment: '',
                                  cashAttachmentName: ''
                                }))
                              }
                            >
                              Remove photo
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {fuelFormError && (
                    <p className="fmct-form-error fmct-form-full" role="alert">
                      {fuelFormError}
                    </p>
                  )}
                  <div className="fmct-form-actions fmct-form-full">
                    <button type="button" className="fmct-btn fmct-btn-ghost" onClick={closeFuelDialog}>
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="fmct-btn fmct-btn-primary"
                      disabled={!fuelVehicleSelected || fuelSaving}
                    >
                      <Fuel className="h-4 w-4" /> {fuelSaving ? 'Saving…' : 'Save Fuel Entry'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDriverVir = () => {
    if (!selectedDriverVehicle) return null;
    const pastInspections = driverVirInspections;
    const hasFail = Object.values(virChecks).some((v) => v === 'fail');

    const openVirInspectDialog = (inspectionDate) => {
      if (selectedVehicleInactive) return;
      setVirInspectTargetDate(inspectionDate);
      setVirChecks(createDefaultVirChecks());
      setVirTickets(createDefaultVirTickets());
      setVirKmReading(String(selectedDriverVehicle.odometer));
      setVirFormError('');
      setVirNotes('');
      setVirAttachment('');
      setVirAttachmentName('');
      setVirInspectDialogOpen(true);
    };

    const closeVirInspectDialog = () => {
      setVirInspectDialogOpen(false);
      setVirInspectTargetDate(null);
      setVirFormError('');
    };

    const updateVirTicket = (ticketId, field, value) => {
      setVirTickets((prev) => ({
        ...prev,
        [ticketId]: {
          ...prev[ticketId],
          [field]: value,
          ...(field === 'hasTicket' && value === 'no' ? { amount: '' } : {})
        }
      }));
    };

    const submitVirInspection = async (e) => {
      e.preventDefault();
      if (!virInspectTargetDate || virSaving || selectedVehicleInactive) return;
      if (!isVirChecklistComplete(virChecks)) {
        setVirFormError('Please tick OK, NOT OKAY, or NA for every inspection item before submitting.');
        return;
      }
      setVirFormError('');
      const todayIso = new Date().toISOString().slice(0, 10);
      const overall = hasFail ? 'fail' : 'pass';
      const virNo = resolveVirNumber({
        vehicleId: selectedDriverVehicle.id,
        date: virInspectTargetDate,
        plate: selectedDriverVehicle.plate
      });
      const payload = {
        virNo,
        overall,
        inspectedDate: todayIso,
        notes: virNotes || (hasFail ? 'Inspection failed.' : 'All items satisfactory.'),
        odometer: Number(virKmReading) || selectedDriverVehicle.odometer,
        tickets: virTickets,
        checks: { ...virChecks },
        attachmentPhoto: virAttachment || null,
        attachmentPhotoName: virAttachmentName || ''
      };

      setVirSubmissionOverrides((prev) => ({
        ...prev,
        [`${selectedDriverVehicle.id}-${virInspectTargetDate}`]: payload
      }));

      setVirSaving(true);
      try {
        const fleetDataId = resolveFleetDataVehicleId(selectedDriverVehicle, vehicles);
        const saved = await upsertVirReport({
          vehicleId: fleetDataId || selectedDriverVehicle.id,
          plate: selectedDriverVehicle.plate,
          driver: selectedDriverVehicle.driver,
          inspector: authSession?.name || selectedDriverVehicle.driver,
          project: selectedDriverVehicle.project,
          inspectionDate: virInspectTargetDate,
          ...payload,
          submittedByEmail: authSession?.email || '',
          submittedByName: authSession?.name || ''
        });
        setVirReportsFromDb((prev) => {
          const next = (prev || []).filter(
            (r) => String(r.date || r.inspectionDate) !== String(virInspectTargetDate)
          );
          next.push(saved);
          return next.sort((a, b) => String(b.date).localeCompare(String(a.date)));
        });
        setAllVirReportsFromDb((prev) => {
          const id = saved?.id;
          const next = (prev || []).filter((r) => {
            if (id && r.id === id) return false;
            return !(
              String(r.vehicleId) === String(saved.vehicleId) &&
              String(r.date || r.inspectionDate) === String(virInspectTargetDate)
            );
          });
          next.push(saved);
          return next.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
        });
        closeVirInspectDialog();
      } catch (err) {
        setVirFormError(getVirReportErrorMessage(err) || 'Failed to save VIR report.');
      } finally {
        setVirSaving(false);
      }
    };

    const renderVirInspectForm = () => {
      const formVirNo = resolveVirNumber({
        vehicleId: selectedDriverVehicle.id,
        date: virInspectTargetDate
      });

      return (
      <form className="fmct-vir-inspect-panel fmct-vir-inspect-panel--dialog" onSubmit={submitVirInspection}>
        <div className="fmct-vir-number-banner">
          <span>VIR No.</span>
          <strong>{formVirNo}</strong>
        </div>
        <label className="fmct-vir-km-reading">
          KM Reading
          <input
            type="number"
            min="0"
            step="1"
            required
            placeholder="Enter current odometer (KM)"
            value={virKmReading}
            onChange={(e) => setVirKmReading(e.target.value)}
          />
        </label>
        <div className="fmct-vir-checklist">
          <div className="fmct-vir-checklist-head">
            <span>Item</span>
            <span>Result</span>
          </div>
          {VIR_CHECKLIST_CATEGORIES.map((category) => (
            <section key={category.id} className="fmct-vir-category">
              <h4 className="fmct-vir-category-title">{category.label}</h4>
              {category.items.map((item) => (
                <div key={item} className="fmct-vir-row">
                  <span>{item}</span>
                  <div className="fmct-vir-options">
                    {getVirCheckOptionsForCategory(category.id).map((opt) => (
                      <label key={opt} className={`fmct-vir-opt fmct-vir-opt--${opt}`}>
                        <input
                          type="radio"
                          name={`vir-${item}`}
                          value={opt}
                          checked={virChecks[item] === opt}
                          onChange={() => {
                            setVirFormError('');
                            setVirChecks((c) => ({ ...c, [item]: opt }));
                          }}
                        />
                        {getVirCheckOptionLabel(opt)}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ))}
          <section className="fmct-vir-category fmct-vir-tickets-section">
            <h4 className="fmct-vir-category-title">TICKETS</h4>
            {VIR_TICKET_ITEMS.map((ticket) => (
              <div key={ticket.id} className="fmct-vir-ticket-row">
                <span>{ticket.label}</span>
                <div className="fmct-vir-ticket-actions">
                  <div className="fmct-vir-options">
                    {['yes', 'no'].map((opt) => (
                      <label key={opt} className={`fmct-vir-opt fmct-vir-opt--${opt}`}>
                        <input
                          type="radio"
                          name={`vir-ticket-${ticket.id}`}
                          value={opt}
                          checked={virTickets[ticket.id].hasTicket === opt}
                          onChange={() => updateVirTicket(ticket.id, 'hasTicket', opt)}
                        />
                        {opt.toUpperCase()}
                      </label>
                    ))}
                  </div>
                  {virTickets[ticket.id].hasTicket === 'yes' && (
                    <label className="fmct-vir-ticket-amount">
                      Amount to be paid (TZS)
                      <input
                        type="number"
                        min="0"
                        step="1"
                        required
                        placeholder="e.g. 50000"
                        value={virTickets[ticket.id].amount}
                        onChange={(e) => updateVirTicket(ticket.id, 'amount', e.target.value)}
                      />
                    </label>
                  )}
                </div>
              </div>
            ))}
          </section>
        </div>
        {virFormError ? <p className="fmct-form-error">{virFormError}</p> : null}
        <div className="fmct-vir-attachment fmct-form-full">
          <span className="fmct-vir-attachment-label">Attachment Photo</span>
          <label className="fmct-vir-attachment-upload">
            <input
              type="file"
              accept="image/*"
              className="fmct-license-upload-input"
              onChange={handleVirAttachmentUpload}
            />
            {virAttachment ? (
              <img src={virAttachment} alt="Inspection attachment preview" className="fmct-vir-attachment-preview" />
            ) : (
              <div className="fmct-vir-attachment-placeholder">
                <Upload className="h-4 w-4" />
                <span>Tap to attach photo</span>
              </div>
            )}
            <span className="fmct-vir-attachment-hint">
              {virAttachmentName || 'Optional — vehicle photo or supporting image'}
            </span>
          </label>
          {virAttachment && (
            <button
              type="button"
              className="fmct-btn fmct-btn-ghost fmct-vir-attachment-remove"
              onClick={() => {
                setVirAttachment('');
                setVirAttachmentName('');
              }}
            >
              Remove photo
            </button>
          )}
        </div>
        <label className="fmct-form-full fmct-vir-notes">
          Notes
          <textarea
            rows={3}
            placeholder="Additional comments…"
            value={virNotes}
            onChange={(e) => setVirNotes(e.target.value)}
          />
        </label>
        <button type="submit" className="fmct-btn fmct-btn-primary" disabled={virSaving}>
          <ClipboardCheck className="h-4 w-4" /> {virSaving ? 'Saving…' : 'Submit Vehicle Inspection Report'}
        </button>
      </form>
      );
    };

    return renderDriverPageShell(
      <div className="fmct-driver-action-page">
        {selectedVehicleInactive && (
          <div className="fmct-card fmct-inactive-vehicle-banner" role="status">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            <div>
              <strong>{selectedDriverVehicle.plate} is inactive</strong>
              <p>Vehicle inspections cannot be submitted for inactive vehicles. Switch to an active vehicle.</p>
            </div>
          </div>
        )}
        <div className="fmct-card fmct-table-card">
          <div className="fmct-table-toolbar">
            <h3>Previous Inspections</h3>
          </div>
          <div className="fmct-table-wrap">
            <table className="fmct-table">
              <thead>
                <tr>
                  <th>VIR No.</th>
                  <th>Date</th>
                  <th>Odometer</th>
                  <th>Inspector</th>
                  <th>Inspected Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pastInspections.map((r) => (
                  <tr key={r.id}>
                    <td className="fmct-vir-no-cell">{r.virNo}</td>
                    <td>{formatLicenseDisplayDate(r.date)}</td>
                    <td>{r.odometer != null ? formatKm(r.odometer) : '—'}</td>
                    <td>{r.inspector}</td>
                    <td>
                      {r.overall === 'inspect'
                        ? '—'
                        : formatLicenseDisplayDate(r.inspectedDate || r.date)}
                    </td>
                    <td>
                      {r.overall === 'inspect' ? (
                        selectedVehicleInactive ? (
                          <span
                            className={`fmct-status fmct-status-${getVirResultStatusClass(r.overall)} fmct-status-disabled`}
                            title="Inactive vehicle — inspection disabled"
                          >
                            {getVirResultLabel(r.overall)}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className={`fmct-status fmct-status-${getVirResultStatusClass(r.overall)} fmct-status-clickable`}
                            onClick={() => openVirInspectDialog(r.date)}
                          >
                            {getVirResultLabel(r.overall)}
                          </button>
                        )
                      ) : (
                        <span className={`fmct-status fmct-status-${getVirResultStatusClass(r.overall)}`}>
                          {getVirResultLabel(r.overall)}
                        </span>
                      )}
                    </td>
                    <td>
                      {r.overall !== 'inspect' && (
                        <button
                          type="button"
                          className="fmct-btn fmct-btn-outline fmct-btn-sm"
                          onClick={() => exportVirPdf(r, selectedDriverVehicle)}
                        >
                          <FileText className="h-4 w-4" /> Download Form
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {virInspectDialogOpen && virInspectTargetDate && (
          <div className="fmct-modal-overlay" onClick={closeVirInspectDialog} role="presentation">
            <div
              className="fmct-modal fmct-vir-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-label="Vehicle inspection"
            >
              <div className="fmct-modal-header">
                <div>
                  <h3>Vehicle Inspection Report</h3>
                  <p>
                    {resolveVirNumber({ vehicleId: selectedDriverVehicle.id, date: virInspectTargetDate })} — {formatLicenseDisplayDate(virInspectTargetDate)} — {selectedDriverVehicle.plate} · {selectedDriverVehicle.driver}
                  </p>
                </div>
                <button type="button" className="fmct-modal-close" onClick={closeVirInspectDialog} aria-label="Close">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="fmct-modal-body">
                {renderVirInspectForm()}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDriverServiceCard = () => {
    if (!selectedDriverVehicle) return null;
    const v = selectedDriverVehicle;
    const fleetDataId = resolveFleetDataVehicleId(v, vehicles);
    const serviceVehicleId = fleetDataId || v.id;
    const latestInspectionKm = getLatestInspectionOdometer(driverVirInspections, v.odometer);
    // Backend ServiceCardsSGM only — mock MAINTENANCE_RECORDS are not shown here.
    const backendServiceRecords = [
      ...(serviceCardsFromDb || []).map((r) => ({
        ...r,
        vehicleId: v.id,
        category: 'Service'
      })),
      ...(serviceCardRecordOverrides[v.id] || [])
    ];
    const serviceCardRows = buildServiceCardRows(v.id, latestInspectionKm, backendServiceRecords);
    const latestServiceRecord = [...backendServiceRecords].sort((a, b) =>
      String(b.date).localeCompare(String(a.date))
    )[0];
    const hasServiceCard = Boolean(latestServiceRecord);
    const lastServiceDate = latestServiceRecord?.date || null;
    const lastServiceOdometer = latestServiceRecord?.servicedOdometer ?? null;
    const nextServiceKm =
      latestServiceRecord?.nextServiceKm != null &&
      Number.isFinite(Number(latestServiceRecord.nextServiceKm))
        ? Number(latestServiceRecord.nextServiceKm)
        : null;
    const kmRemaining =
      nextServiceKm != null && latestInspectionKm != null
        ? nextServiceKm - latestInspectionKm
        : null;
    const isServiceDue = kmRemaining != null && kmRemaining <= 0;
    const currentCardStatus = !hasServiceCard
      ? { label: 'No service card', status: 'pending' }
      : getServiceCardIntervalStatus(nextServiceKm, latestInspectionKm);

    const openServiceCardDialog = () => {
      if (selectedVehicleInactive) return;
      const suggestedNext =
        (Number(latestInspectionKm) || Number(v.odometer) || 0) + SERVICE_INTERVAL_KM;
      setServiceCardForm({
        date: new Date().toISOString().slice(0, 10),
        servicedMileage: String(latestInspectionKm || v.odometer),
        nextServiceKm: String(suggestedNext),
        notes: ''
      });
      setServiceCardFormError('');
      setServiceCardDialogOpen(true);
    };

    const closeServiceCardDialog = () => {
      setServiceCardDialogOpen(false);
      setServiceCardFormError('');
    };

    const submitServiceCardRecord = async (e) => {
      e.preventDefault();
      if (serviceCardSaving || selectedVehicleInactive) return;
      const servicedMileage = Number(serviceCardForm.servicedMileage);
      const nextService = Number(serviceCardForm.nextServiceKm);
      if (!serviceCardForm.date) {
        setServiceCardFormError('Please enter the date serviced.');
        return;
      }
      if (!Number.isFinite(servicedMileage) || servicedMileage <= 0) {
        setServiceCardFormError('Please enter a valid serviced mileage.');
        return;
      }
      if (!Number.isFinite(nextService) || nextService <= 0) {
        setServiceCardFormError('Please enter the next service mileage.');
        return;
      }
      if (nextService <= servicedMileage) {
        setServiceCardFormError('Next service KM must be greater than serviced mileage.');
        return;
      }
      setServiceCardFormError('');
      setServiceCardSaving(true);
      try {
        const saved = await createServiceCard({
          vehicleId: serviceVehicleId,
          plate: v.plate,
          driver: v.driver,
          project: v.project,
          serviceDate: serviceCardForm.date,
          servicedMileage,
          nextServiceKm: nextService,
          notes: serviceCardForm.notes.trim(),
          item: 'Service',
          status: 'completed',
          submittedByEmail: authSession?.email || '',
          submittedByName: authSession?.name || ''
        });
        setServiceCardsFromDb((prev) =>
          [saved, ...(prev || [])].sort((a, b) => String(b.date).localeCompare(String(a.date)))
        );
        closeServiceCardDialog();
      } catch (err) {
        // Keep a local copy only if backend save fails, so the driver still sees the entry.
        const localRecord = {
          id: `sc-${v.id}-${Date.now()}`,
          vehicleId: v.id,
          plate: v.plate,
          date: serviceCardForm.date,
          item: 'Service',
          category: 'Service',
          supplier: '—',
          cost: 0,
          invoiceNumber: '—',
          attachment: null,
          status: 'completed',
          servicedOdometer: servicedMileage,
          nextServiceKm: nextService,
          notes: serviceCardForm.notes.trim()
        };
        setServiceCardRecordOverrides((prev) => ({
          ...prev,
          [v.id]: [...(prev[v.id] || []), localRecord]
        }));
        setServiceCardFormError(getServiceCardErrorMessage(err) || 'Failed to save service card.');
      } finally {
        setServiceCardSaving(false);
      }
    };

    return renderDriverPageShell(
      <div className="fmct-driver-action-page">
        {selectedVehicleInactive && (
          <div className="fmct-card fmct-inactive-vehicle-banner" role="status">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            <div>
              <strong>{v.plate} is inactive</strong>
              <p>Service cards cannot be recorded for inactive vehicles. Switch to an active vehicle.</p>
            </div>
          </div>
        )}
        <div className="fmct-service-card-stats">
          <div>
            <span>Last Service</span>
            <strong>{hasServiceCard ? formatLicenseDisplayDate(lastServiceDate) : '—'}</strong>
            <small>{hasServiceCard && lastServiceOdometer != null ? formatKm(lastServiceOdometer) : 'No record yet'}</small>
          </div>
          <div>
            <span>Next Service Due</span>
            <strong>
              {!hasServiceCard
                ? '—'
                : isServiceDue
                  ? 'Overdue'
                  : formatKm(nextServiceKm)}
            </strong>
            <small>
              {!hasServiceCard
                ? 'Record a service card to begin'
                : isServiceDue
                  ? 'Service required now'
                  : `${Math.max(kmRemaining || 0, 0)} KM remaining`}
            </small>
          </div>
          <div>
            <span>Status</span>
            <strong>
              <span className={`fmct-status fmct-status-${currentCardStatus.status}`}>
                {currentCardStatus.label}
              </span>
            </strong>
            <small>Last inspection: {formatKm(latestInspectionKm)}</small>
          </div>
        </div>

        <div className="fmct-card fmct-table-card">
          <div className="fmct-table-toolbar">
            <div>
              <h3>Service Card</h3>
              <p className="fmct-toolbar-sub">
                Status compares last inspection mileage ({formatKm(latestInspectionKm)}) with next service due
              </p>
            </div>
            <button
              type="button"
              className="fmct-btn fmct-btn-primary"
              onClick={openServiceCardDialog}
              disabled={selectedVehicleInactive}
              title={selectedVehicleInactive ? 'Inactive vehicle — service card disabled' : undefined}
            >
              <Plus className="h-4 w-4" /> Record Service Card
            </button>
          </div>
          <div className="fmct-table-wrap">
            <table className="fmct-table">
              <thead>
                <tr>
                  <th>Date Serviced</th>
                  <th>Serviced Mileage</th>
                  <th>Next Service</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {serviceCardRows.length === 0 ? (
                  <tr>
                    <td colSpan={4}>No service records on file.</td>
                  </tr>
                ) : (
                  serviceCardRows.map((row) => (
                    <tr key={row.id}>
                      <td>{formatLicenseDisplayDate(row.date)}</td>
                      <td>{formatKm(row.servicedMileage)}</td>
                      <td>{formatKm(row.nextServiceKm)}</td>
                      <td>
                        <span className={`fmct-status fmct-status-${row.statusClass}`}>
                          {row.statusLabel}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {serviceCardDialogOpen && (
          <div className="fmct-modal-overlay" onClick={closeServiceCardDialog} role="presentation">
            <div
              className="fmct-modal fmct-service-card-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-label="Record service card"
            >
              <div className="fmct-modal-header">
                <div>
                  <h3>Record Service Card</h3>
                  <p>
                    {v.plate} · {v.model} · {v.driver}
                  </p>
                </div>
                <button type="button" className="fmct-modal-close" onClick={closeServiceCardDialog} aria-label="Close">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="fmct-modal-body">
                <form className="fmct-form-grid fmct-service-card-form" onSubmit={submitServiceCardRecord}>
                  <label>
                    Date Serviced
                    <input
                      type="date"
                      required
                      value={serviceCardForm.date}
                      onChange={(e) => setServiceCardForm((s) => ({ ...s, date: e.target.value }))}
                    />
                  </label>
                  <label>
                    Serviced Mileage (KM)
                    <input
                      type="number"
                      min="1"
                      step="1"
                      required
                      placeholder="Odometer reading at service"
                      value={serviceCardForm.servicedMileage}
                      onChange={(e) => setServiceCardForm((s) => ({ ...s, servicedMileage: e.target.value }))}
                    />
                  </label>
                  <label>
                    Next Service (KM)
                    <input
                      type="number"
                      min="1"
                      step="1"
                      required
                      placeholder="Enter next service odometer"
                      value={serviceCardForm.nextServiceKm}
                      onChange={(e) => setServiceCardForm((s) => ({ ...s, nextServiceKm: e.target.value }))}
                    />
                  </label>
                  <label className="fmct-form-full">
                    Notes
                    <textarea
                      rows={3}
                      placeholder="Optional service notes"
                      value={serviceCardForm.notes}
                      onChange={(e) => setServiceCardForm((s) => ({ ...s, notes: e.target.value }))}
                    />
                  </label>
                  {serviceCardFormError && (
                    <p className="fmct-form-error fmct-form-full" role="alert">
                      {serviceCardFormError}
                    </p>
                  )}
                  <div className="fmct-form-actions fmct-form-full">
                    <button type="button" className="fmct-btn fmct-btn-ghost" onClick={closeServiceCardDialog}>
                      Cancel
                    </button>
                    <button type="submit" className="fmct-btn fmct-btn-primary" disabled={serviceCardSaving}>
                      <Save className="h-4 w-4" /> {serviceCardSaving ? 'Saving…' : 'Save Service Record'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDriverDrivingLicense = () => {
    if (!selectedDriverVehicle) return null;
    const v = selectedDriverVehicle;
    const fleetDataId = resolveFleetDataVehicleId(v, vehicles);
    const licenseKey = fleetDataId || v.id;
    const storedLicense = getEffectiveLicense(licenseKey, v.plate) || getEffectiveLicense(v.id, v.plate);
    const blankLicense = {
      licenseNumber: '',
      fullName: v.driver || '',
      licenseClass: '',
      issueDate: '',
      expiryDate: '',
      issuingAuthority: 'Tanzania Revenue Authority (TRA)',
      nationalId: '',
      frontImage: '/assets/driving-licenses/dl-front.svg',
      frontImageName: ''
    };
    const license = storedLicense || (isLicenseEditing ? { ...blankLicense, ...(licenseDraft || {}) } : null);

    if (!license) {
      return renderDriverPageShell(
        <div className="fmct-card fmct-placeholder">
          <h3>No driving license on file</h3>
          <p>Add license details for {v.driver}.</p>
          <button
            type="button"
            className="fmct-btn fmct-btn-primary"
            onClick={() => startLicenseEdit(blankLicense)}
          >
            <Plus className="h-4 w-4" /> Add driving license
          </button>
        </div>
      );
    }

    const licenseStatus = getLicenseStatus(license.expiryDate || '2099-01-01');
    const draft = licenseDraft || {};

    const updateDraft = (field, value) => {
      setLicenseDraft((prev) => ({ ...(prev || {}), [field]: value }));
    };

    return renderDriverPageShell(
      <div className="fmct-driver-license-page">
        <div className="fmct-license-edit-toolbar">
          {!isLicenseEditing ? (
            <div className="fmct-license-edit-actions">
              <button
                type="button"
                className="fmct-btn fmct-btn-outline"
                onClick={() => startLicenseEdit(license)}
              >
                <Pencil className="h-4 w-4" /> Edit information
              </button>
              <button
                type="button"
                className="fmct-btn fmct-btn-outline"
                onClick={() => exportDrivingLicensePdf(license, v)}
              >
                <FileText className="h-4 w-4" /> Download PDF
              </button>
            </div>
          ) : (
            <div className="fmct-license-edit-actions">
              <button
                type="button"
                className="fmct-btn fmct-btn-primary"
                disabled={licenseSaving}
                onClick={() => saveLicenseEdit(licenseKey, { plate: v.plate, driver: v.driver })}
              >
                <Save className="h-4 w-4" /> {licenseSaving ? 'Saving…' : 'Save changes'}
              </button>
              <button type="button" className="fmct-btn fmct-btn-ghost" onClick={cancelLicenseEdit}>
                <X className="h-4 w-4" /> Cancel
              </button>
            </div>
          )}
        </div>

        <div className="fmct-driver-panel-info-grid">
          <section className="fmct-driver-panel-section">
            <h4>License Details</h4>
            {isLicenseEditing ? (
              <div className="fmct-license-form">
                <label>License number<input value={draft.licenseNumber || ''} onChange={(e) => updateDraft('licenseNumber', e.target.value)} /></label>
                <label>Full name<input value={draft.fullName || ''} onChange={(e) => updateDraft('fullName', e.target.value)} /></label>
                <label>Class<input value={draft.licenseClass || ''} onChange={(e) => updateDraft('licenseClass', e.target.value)} /></label>
              </div>
            ) : (
              <dl className="fmct-dl">
                <dt>License number</dt><dd>{license.licenseNumber}</dd>
                <dt>Full name</dt><dd>{license.fullName}</dd>
                <dt>Class</dt><dd>{license.licenseClass}</dd>
                <dt>Status</dt>
                <dd>
                  <span className={`fmct-status fmct-status-${licenseStatus.key === 'valid' ? 'completed' : licenseStatus.key === 'expiring' ? 'pending' : 'repair'}`}>
                    {licenseStatus.label}
                    {licenseStatus.days >= 0 ? ` (${licenseStatus.days} days)` : ''}
                  </span>
                </dd>
              </dl>
            )}
          </section>

          <section className="fmct-driver-panel-section">
            <h4>Validity &amp; Authority</h4>
            {isLicenseEditing ? (
              <div className="fmct-license-form">
                <label>Issue date<input type="date" value={draft.issueDate || ''} onChange={(e) => updateDraft('issueDate', e.target.value)} /></label>
                <label>Expiry date<input type="date" value={draft.expiryDate || ''} onChange={(e) => updateDraft('expiryDate', e.target.value)} /></label>
                <label>Issuing authority<input value={draft.issuingAuthority || ''} onChange={(e) => updateDraft('issuingAuthority', e.target.value)} /></label>
              </div>
            ) : (
              <dl className="fmct-dl">
                <dt>Issue date</dt><dd>{formatLicenseDisplayDate(license.issueDate)}</dd>
                <dt>Expiry date</dt><dd>{formatLicenseDisplayDate(license.expiryDate)}</dd>
                <dt>Issuing authority</dt><dd>{license.issuingAuthority}</dd>
              </dl>
            )}
          </section>
        </div>

        <section className="fmct-driver-panel-section fmct-dl-images-section">
          <h4>Driving License — Front View</h4>
          {isLicenseEditing ? (
            <div className="fmct-license-image-upload">
              <label className="fmct-license-upload-label">
                <input
                  type="file"
                  accept="image/*"
                  className="fmct-license-upload-input"
                  onChange={handleLicenseImageUpload}
                />
                <span className="fmct-btn fmct-btn-outline fmct-license-upload-btn">
                  <Upload className="h-4 w-4" /> Upload front image
                </span>
              </label>
              <p className="fmct-license-upload-hint">
                {draft.frontImageName
                  ? `Selected: ${draft.frontImageName}`
                  : 'Choose an image — the preview below updates as soon as you select a file.'}
              </p>
            </div>
          ) : null}
          <figure className="fmct-dl-image-card fmct-dl-image-card--single">
            <img
              src={resolveLicenseImageSrc(isLicenseEditing ? draft.frontImage : license.frontImage)}
              alt={`${license.fullName} driving license front`}
              className="fmct-dl-image"
            />
          </figure>
        </section>
      </div>
    );
  };

  const renderAdminDrivingLicensesView = () => (
    <div className="fmct-card fmct-table-card">
      <div className="fmct-table-toolbar">
        <div>
          <h3>Driving licenses</h3>
          <p className="fmct-toolbar-sub">Click a row to view full license details on the right.</p>
        </div>
      </div>
      <div className="fmct-table-wrap">
        <table className="fmct-table">
          <thead>
            <tr>
              <th>Full name</th>
              <th>License number</th>
              <th>Issue date</th>
              <th>Expiry date</th>
            </tr>
          </thead>
          <tbody>
            {adminLicenseRows.map((row) => {
              const { license, vehicleId } = row;
              const isSelected = drawerLicense?.vehicleId === vehicleId;
              return (
                <tr
                  key={vehicleId}
                  className={`fmct-row-clickable${isSelected ? ' fmct-row-selected' : ''}`}
                  onClick={() => openLicenseDrawer(row)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openLicenseDrawer(row);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-pressed={isSelected}
                >
                  <td><strong>{license.fullName}</strong></td>
                  <td>{license.licenseNumber}</td>
                  <td>{formatLicenseDisplayDate(license.issueDate)}</td>
                  <td>{formatLicenseDisplayDate(license.expiryDate)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const moneyFieldValue = (n) => {
    if (n === null || n === undefined || n === '' || Number(n) === 0) return '';
    return Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const persistFleetFuelInvoiceRow = async (rowId) => {
    const nextRow = fleetFuelInvoiceSnapshotsRef.current[rowId];
    if (!rowId || !nextRow) return;
    setFleetFuelInvoiceSavingIds((prev) => {
      const next = new Set(prev);
      next.add(rowId);
      return next;
    });
    setFleetFuelInvoicesError('');
    try {
      const updated = await updateFleetFuelInvoice(rowId, nextRow);
      fleetFuelInvoiceSnapshotsRef.current[rowId] = updated;
      setFleetFuelInvoices((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch (err) {
      setFleetFuelInvoicesError(
        getFleetFuelInvoiceErrorMessage(err) || 'Failed to save fuel invoice.'
      );
    } finally {
      setFleetFuelInvoiceSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
    }
  };

  const scheduleFleetFuelInvoicePersist = (rowId) => {
    if (!rowId) return;
    const timers = fleetFuelInvoiceSaveTimers.current;
    if (timers[rowId]) clearTimeout(timers[rowId]);
    timers[rowId] = setTimeout(() => {
      delete timers[rowId];
      persistFleetFuelInvoiceRow(rowId);
    }, 600);
  };

  const updateFleetFuelInvoiceCell = (rowId, field, rawValue) => {
    setFleetFuelInvoices((prev) => {
      const current = prev.find((r) => r.id === rowId);
      if (!current) return prev;

      const moneyFields = new Set(['fuelAmount', 'managementFee', 'vat']);
      let nextValue = rawValue;
      if (moneyFields.has(field)) {
        const cleaned = String(rawValue ?? '').replace(/,/g, '').trim();
        nextValue = cleaned === '' ? 0 : Number(cleaned);
        if (!Number.isFinite(nextValue)) nextValue = current[field] || 0;
      }

      const patched = { ...current, [field]: nextValue };
      if (moneyFields.has(field)) {
        patched.totalInvoiceAmount = calcFuelInvoiceTotal(
          patched.fuelAmount,
          patched.managementFee,
          patched.vat
        );
      }

      fleetFuelInvoiceSnapshotsRef.current[rowId] = patched;
      scheduleFleetFuelInvoicePersist(rowId);

      return prev.map((r) => (r.id === rowId ? patched : r));
    });
  };

  const addFleetFuelInvoiceRow = async () => {
    setFleetFuelInvoiceAdding(true);
    setFleetFuelInvoicesError('');
    try {
      const minSort = fleetFuelInvoices.reduce(
        (min, r) => Math.min(min, Number(r.sortOrder) || 0),
        0
      );
      const created = await createFleetFuelInvoice({
        ...emptyFuelInvoiceForm(),
        project: projectFilter !== 'all' ? projectFilter : '',
        paymentStatus: 'Not processed',
        fuelAmount: 0,
        managementFee: 0,
        vat: 0,
        sortOrder: minSort - 10
      });
      fleetFuelInvoiceSnapshotsRef.current[created.id] = created;
      setFleetFuelInvoices((prev) => [created, ...prev]);
    } catch (err) {
      setFleetFuelInvoicesError(
        getFleetFuelInvoiceErrorMessage(err) || 'Failed to add fuel invoice row.'
      );
    } finally {
      setFleetFuelInvoiceAdding(false);
    }
  };

  const removeFleetFuelInvoice = async (row) => {
    if (!row?.id) return;
    if (!window.confirm(`Delete invoice ${row.invoiceNo || '(blank)'} for ${row.project || 'this row'}?`)) {
      return;
    }
    const timers = fleetFuelInvoiceSaveTimers.current;
    if (timers[row.id]) {
      clearTimeout(timers[row.id]);
      delete timers[row.id];
    }
    try {
      await deleteFleetFuelInvoice(row.id);
      delete fleetFuelInvoiceSnapshotsRef.current[row.id];
      setFleetFuelInvoices((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err) {
      setFleetFuelInvoicesError(getFleetFuelInvoiceErrorMessage(err) || 'Failed to delete.');
    }
  };

  const renderAdminFuelView = () => {
    const filteredFuelInvoices =
      projectFilter === 'all'
        ? fleetFuelInvoices
        : fleetFuelInvoices.filter(
            (row) => String(row.project || '').trim() === projectFilter
          );

    return (
    <div className="fmct-card fmct-table-card">
      <div className="fmct-table-toolbar">
        <div>
          <h3>Fuel invoices</h3>
          <p className="fmct-toolbar-sub">
            Click any cell to edit. Changes save automatically. Use Add row for a new invoice.
            {projectFilter !== 'all' ? (
              <>
                {' '}
                Showing <strong>{projectFilter}</strong>.
              </>
            ) : null}
          </p>
        </div>
        <button
          type="button"
          className="fmct-btn fmct-btn-primary"
          onClick={addFleetFuelInvoiceRow}
          disabled={fleetFuelInvoiceAdding}
        >
          <Plus className="h-4 w-4" /> {fleetFuelInvoiceAdding ? 'Adding…' : 'Add row'}
        </button>
      </div>
      {fleetFuelInvoicesError && (
        <p className="fmct-fuel-invoice-error">{fleetFuelInvoicesError}</p>
      )}
      <div className="fmct-table-wrap">
        <table className="fmct-table fmct-table--fuel-invoices fmct-table--fuel-invoices-editable">
          <thead>
            <tr>
              <th>Invoice No</th>
              <th>Month</th>
              <th>Year</th>
              <th>Project</th>
              <th>Fuel amount</th>
              <th>Management fee</th>
              <th>VAT</th>
              <th>Total invoice amount</th>
              <th>PO</th>
              <th>Payment status</th>
              <th>Date paid</th>
              <th>PR No - Jazzcom</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {fleetFuelInvoicesLoading ? (
              <tr>
                <td colSpan={13} className="fmct-table-empty">
                  Loading fuel invoices…
                </td>
              </tr>
            ) : filteredFuelInvoices.length === 0 ? (
              <tr>
                <td colSpan={13} className="fmct-table-empty">
                  {fleetFuelInvoices.length === 0 ? (
                    <>
                      No fuel invoices yet. Click <strong>Add row</strong> to start.
                    </>
                  ) : (
                    <>
                      No invoices for <strong>{projectFilter}</strong>. Choose another project
                      or click <strong>Add row</strong>.
                    </>
                  )}
                </td>
              </tr>
            ) : (
              filteredFuelInvoices.map((row) => {
                const total = calcFuelInvoiceTotal(
                  row.fuelAmount,
                  row.managementFee,
                  row.vat
                );
                const saving = fleetFuelInvoiceSavingIds.has(row.id);
                const projectSelectOptions = [
                  ...new Set(
                    [row.project, ...fuelInvoiceProjectOptions].map((p) => String(p || '').trim()).filter(Boolean)
                  )
                ];
                return (
                  <tr key={row.id} className={saving ? 'fmct-fuel-row-saving' : undefined}>
                    <td className="fmct-fuel-invoice-no-cell">
                      <input
                        className="fmct-cell-input fmct-cell-input--invoice-no"
                        type="text"
                        value={row.invoiceNo || ''}
                        onChange={(e) =>
                          updateFleetFuelInvoiceCell(row.id, 'invoiceNo', e.target.value)
                        }
                        title={row.invoiceNo || ''}
                      />
                    </td>
                    <td>
                      <select
                        className="fmct-cell-input"
                        value={row.month || ''}
                        onChange={(e) =>
                          updateFleetFuelInvoiceCell(row.id, 'month', e.target.value)
                        }
                      >
                        <option value="" />
                        {FUEL_INVOICE_MONTHS.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="fmct-cell-input fmct-cell-input--year"
                        value={row.year || ''}
                        onChange={(e) =>
                          updateFleetFuelInvoiceCell(row.id, 'year', e.target.value)
                        }
                        aria-label="Year"
                      >
                        <option value="" />
                        {fuelInvoiceYearOptions.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="fmct-fuel-project-cell">
                      <div
                        className={`fmct-fuel-project-wrap${row.project ? '' : ' is-empty'}`}
                        style={{
                          width: `${cellInputWidthCh(row.project, 10)}ch`,
                          minWidth: `${cellInputWidthCh(row.project, 10)}ch`,
                        }}
                      >
                        {row.project ? (
                          <span
                            className={`rental-project-badge ${fuelProjectBadgeClass(row.project)}`}
                          >
                            {row.project}
                          </span>
                        ) : null}
                        <select
                          className="fmct-cell-input fmct-cell-input--project"
                          value={row.project || ''}
                          onChange={(e) =>
                            updateFleetFuelInvoiceCell(row.id, 'project', e.target.value)
                          }
                          aria-label="Project"
                          title={row.project || 'Select project'}
                        >
                          <option value="" />
                          {projectSelectOptions.map((p) => (
                            <option key={p} value={p}>
                              {p}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td className="fmct-num">
                      <span className="fmct-cell-money">
                        {moneyFieldValue(row.fuelAmount) !== '' && (
                          <span className="fmct-cell-money-prefix">TSh</span>
                        )}
                        <input
                          className="fmct-cell-input fmct-cell-input--money"
                          type="text"
                          inputMode="numeric"
                          value={moneyFieldValue(row.fuelAmount)}
                          onChange={(e) =>
                            updateFleetFuelInvoiceCell(row.id, 'fuelAmount', e.target.value)
                          }
                        />
                      </span>
                    </td>
                    <td className="fmct-num">
                      <span className="fmct-cell-money">
                        {moneyFieldValue(row.managementFee) !== '' && (
                          <span className="fmct-cell-money-prefix">TSh</span>
                        )}
                        <input
                          className="fmct-cell-input fmct-cell-input--money"
                          type="text"
                          inputMode="numeric"
                          value={moneyFieldValue(row.managementFee)}
                          onChange={(e) =>
                            updateFleetFuelInvoiceCell(row.id, 'managementFee', e.target.value)
                          }
                        />
                      </span>
                    </td>
                    <td className="fmct-num">
                      <span className="fmct-cell-money">
                        {moneyFieldValue(row.vat) !== '' && (
                          <span className="fmct-cell-money-prefix">TSh</span>
                        )}
                        <input
                          className="fmct-cell-input fmct-cell-input--money"
                          type="text"
                          inputMode="numeric"
                          value={moneyFieldValue(row.vat)}
                          onChange={(e) =>
                            updateFleetFuelInvoiceCell(row.id, 'vat', e.target.value)
                          }
                        />
                      </span>
                    </td>
                    <td className="fmct-num fmct-fuel-total-cell">
                      {total > 0 ? formatTSh(total) : ''}
                    </td>
                    <td className="fmct-fuel-po-cell">
                      <input
                        className="fmct-cell-input fmct-cell-input--po"
                        type="text"
                        value={row.po || ''}
                        onChange={(e) =>
                          updateFleetFuelInvoiceCell(row.id, 'po', e.target.value)
                        }
                        style={{ width: `${cellInputWidthCh(row.po)}ch` }}
                        title={row.po || ''}
                      />
                    </td>
                    <td className="fmct-fuel-status-cell">
                      <div className="fmct-fuel-status-wrap">
                        <span
                          className={`fmct-fuel-status-badge ${fuelPaymentStatusClass(
                            row.paymentStatus
                          )}`}
                        >
                          {row.paymentStatus || 'Not processed'}
                        </span>
                        <select
                          className="fmct-cell-input fmct-cell-input--status"
                          value={row.paymentStatus || 'Not processed'}
                          onChange={(e) =>
                            updateFleetFuelInvoiceCell(row.id, 'paymentStatus', e.target.value)
                          }
                          aria-label="Payment status"
                        >
                          {FUEL_INVOICE_PAYMENT_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td className="fmct-fuel-date-cell">
                      <div
                        className={`fmct-cell-date-wrap${row.datePaid ? '' : ' is-empty'}`}
                      >
                        {row.datePaid ? (
                          <span className="fmct-cell-date-label" aria-hidden>
                            {row.datePaid}
                          </span>
                        ) : null}
                        <input
                          className="fmct-cell-input fmct-cell-input--date"
                          type="date"
                          value={fuelDatePaidToInputValue(row.datePaid)}
                          onChange={(e) => {
                            const label = fuelDateInputToPaidLabel(e.target.value);
                            updateFleetFuelInvoiceCell(row.id, 'datePaid', label);
                          }}
                          onClick={(e) => {
                            try {
                              e.currentTarget.showPicker?.();
                            } catch {
                              /* ignore browsers that block showPicker */
                            }
                          }}
                          aria-label="Date paid"
                          title={row.datePaid || ''}
                        />
                      </div>
                    </td>
                    <td className="fmct-fuel-pr-jazzcom-cell">
                      <input
                        className="fmct-cell-input fmct-cell-input--pr-jazzcom"
                        type="text"
                        value={row.prNoJazzcom || ''}
                        onChange={(e) =>
                          updateFleetFuelInvoiceCell(row.id, 'prNoJazzcom', e.target.value)
                        }
                        aria-label="PR No - Jazzcom"
                        title={row.prNoJazzcom || ''}
                      />
                    </td>
                    <td className="fmct-fuel-invoice-actions">
                      <button
                        type="button"
                        className="fmct-link-btn fmct-fuel-delete-btn"
                        onClick={() => removeFleetFuelInvoice(row)}
                        aria-label="Delete row"
                        title="Delete row"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
    );
  };

  const renderAdminDriversView = () => (
    <>
      <div className="fmct-drivers-toolbar">
        <label className="fmct-drivers-search" htmlFor="drivers-card-search">
          <Search className="h-4 w-4" aria-hidden />
          <input
            id="drivers-card-search"
            type="search"
            placeholder="Search driver, car, project…"
            value={driversOverviewSearch}
            onChange={(e) => setDriversOverviewSearch(e.target.value)}
          />
        </label>
      </div>
      {carRentalsLoading && (
        <p className="fmct-rental-sync-note">Loading drivers and cars from Car Rental Invoicing…</p>
      )}
      <div className="fmct-driver-project-groups">
        {adminDriversByProject.length === 0 ? (
          <div className="fmct-card fmct-placeholder">
            <h3>No drivers found</h3>
            <p>Try another search or change the project filter.</p>
          </div>
        ) : (
          adminDriversByProject.map((group) => (
            <section key={group.project} className="fmct-driver-project-section">
              <div className="fmct-driver-project-header">
                <h3>{group.project}</h3>
                <span>{group.entries.length} driver{group.entries.length === 1 ? '' : 's'}</span>
              </div>
              <div className="fmct-drivers-grid">
                {group.entries.map((entry) => {
                  const v = entry.representativeVehicle;
                  return (
                    <div key={entry.key} className="fmct-driver-card">
                      <div className="fmct-driver-card-head">
                        <div>
                          <h3>{entry.driver}</h3>
                          <p>{entry.vehiclesUsedCount} car{entry.vehiclesUsedCount > 1 ? 's' : ''} used</p>
                        </div>
                        <div className="fmct-driver-status-edit fmct-driver-status-edit--head">
                          <select
                            id={`driver-status-${entry.key}`}
                            className={`fmct-driver-status-select fmct-driver-status-select-${getDriverStatus(entry, v.displayStatus)}`}
                            value={getDriverStatus(entry, v.displayStatus)}
                            onChange={(e) => updateDriverStatus(entry, e.target.value)}
                            aria-label={`Status for ${entry.driver}`}
                          >
                            <option value="active">Active</option>
                            <option value="repair">In Repair</option>
                            <option value="due-service">Due Service</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                      </div>
                      <dl className="fmct-dl">
                        <dt>Vehicles used</dt><dd>{entry.vehiclesUsedCount}</dd>
                        <dt>Plate numbers</dt>
                        <dd className="fmct-driver-plates-list">{entry.plates.join(', ')}</dd>
                        <dt>Odometer</dt><dd>{formatKm(v.odometer)}</dd>
                        <dt>Next service</dt>
                        <dd>
                          {v.nextServiceDue.isDue
                            ? 'Overdue'
                            : `${v.nextServiceDue.kmRemaining} KM remaining`}
                        </dd>
                        <dt>Phone</dt><dd>{v.phone || '—'}</dd>
                      </dl>
                      <button type="button" className="fmct-btn fmct-btn-sm fmct-btn-outline" onClick={() => openVehicleDrawer(v)}>
                        View Driver
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </div>
    </>
  );

  const renderPlaceholder = (title, desc) => (
    <div className="fmct-card fmct-placeholder">
      <h3>{title}</h3>
      <p>{desc}</p>
    </div>
  );

  const renderMain = () => {
    switch (activeNav) {
      case 'dashboard':
        if (!isAdminView) {
          return renderDriverDashboard();
        }
        return (
          <>
            {renderKpis()}
            {renderCharts()}
            {renderWidgets()}
          </>
        );
      case 'vehicles':
        return (
          <>
            {renderFilters()}
            {renderVehicleTable()}
          </>
        );
      case 'maintenance':
        if (!isAdminView) return renderDriverMaintenance();
        return (
          <>
            {renderFilters()}
            {renderMaintenanceTable()}
          </>
        );
      case 'breakdown':
        if (!isAdminView) return renderDriverBreakdown();
        return null;
      case 'fuel':
        if (!isAdminView) return renderDriverFuel();
        return renderAdminFuelView();
      case 'vir':
        if (!isAdminView) return renderDriverVir();
        return null;
      case 'service-card':
        if (!isAdminView) return renderDriverServiceCard();
        return null;
      case 'driving-license':
        if (!isAdminView) return renderDriverDrivingLicense();
        return renderAdminDrivingLicensesView();
      case 'drivers':
        if (isAdminView) return renderAdminDriversView();
        return null;
      case 'reports':
        return (
          <div className="fmct-card fmct-placeholder">
            <h3>Reports</h3>
            <p>Generate fleet maintenance summaries and exports.</p>
            <div className="fmct-action-row">
              <button type="button" className="fmct-btn fmct-btn-primary" onClick={exportPdf}>
                <Download className="h-4 w-4" /> Generate PDF Report
              </button>
              <button type="button" className="fmct-btn fmct-btn-outline" onClick={exportMaintenanceExcel}>
                <FileSpreadsheet className="h-4 w-4" /> Export Maintenance Excel
              </button>
            </div>
          </div>
        );
      case 'settings':
        return renderPlaceholder('Settings', 'Configure service intervals, alert thresholds, and suppliers.');
      case 'notifications':
        return renderNotifications();
      default:
        return null;
    }
  };

  return (
    <div className={`fmct-page${isDriverAccount ? ' fmct-page--driver-view' : ''}`}>
      <div className="fmct-scroll">
        <div className="fmct-panel">
          <AppBar
            variant="inset"
            breadcrumbs={[
              { label: 'Platform', hideOnMobile: true },
              { label: 'Sagemcom', to: '/sagemcom' },
              { label: 'Fleet dashboard' }
            ]}
          />

          <section className="fmct-intro" aria-label="Fleet dashboard overview">
            {isDriverAccount ? (
              <div className="fmct-driver-identity-header">
                {(() => {
                  const profile = selectedDriverVehicle
                    ? getDriverProfileWithRental(selectedDriverVehicle)
                    : null;
                  const avatarSrc = resolveLicenseImageSrc(profile?.driverAvatar);
                  const driverName =
                    profile?.driver ||
                    selectedDriverVehicle?.driver ||
                    authSession?.name ||
                    'Driver';
                  const currentProject =
                    profile?.vehicleProject ||
                    selectedDriverVehicle?.project ||
                    '—';
                  const initials = String(driverName)
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((p) => p[0]?.toUpperCase() || '')
                    .join('');
                  return (
                    <>
                      <div className="fmct-driver-identity-avatar">
                        {avatarSrc ? (
                          <img
                            src={avatarSrc}
                            alt=""
                            className="fmct-driver-identity-avatar-img"
                          />
                        ) : (
                          <span className="fmct-driver-identity-avatar-fallback">
                            {initials || <User className="h-4 w-4" />}
                          </span>
                        )}
                      </div>
                      <div className="fmct-driver-identity-text">
                        <h1 className="fmct-driver-identity-name">{driverName}</h1>
                        <p className="fmct-driver-identity-project">{currentProject}</p>
                      </div>
                      <button
                        type="button"
                        className="fmct-driver-identity-logout"
                        onClick={handleDriverLogout}
                        aria-label="Logout"
                        title="Logout"
                      >
                        <LogOut className="h-4 w-4" aria-hidden />
                      </button>
                    </>
                  );
                })()}
              </div>
            ) : (
              <>
                <div className="fmct-intro-text">
                  <h1 className="fmct-intro-title">Fleet dashboard</h1>
                  <p className="fmct-intro-subtitle">
                    {isAdminView
                      ? 'Full fleet overview — costs, repairs, invoices, and performance'
                      : 'Drivers view preview — use Admin View to return'}
                  </p>
                </div>
                <div className="fmct-intro-actions">
                  <div className="fmct-view-toggle" role="group" aria-label="View mode">
                    <button
                      type="button"
                      className={`fmct-view-btn ${viewMode === 'admin' ? 'active' : ''}`}
                      onClick={() => switchViewMode('admin')}
                    >
                      <Shield className="h-3.5 w-3.5" /> Admin View
                    </button>
                    <button
                      type="button"
                      className={`fmct-view-btn ${viewMode === 'drivers' ? 'active' : ''}`}
                      onClick={() => switchViewMode('drivers')}
                    >
                      <Users className="h-3.5 w-3.5" /> Drivers View
                    </button>
                  </div>
                  {isAdminView && (
                    <button
                      type="button"
                      className={`fmct-header-notify-btn${activeNav === 'notifications' ? ' is-active' : ''}`}
                      onClick={() => setActiveNav('notifications')}
                      aria-label={
                        notifications.length > 0
                          ? `Open notifications (${notifications.length})`
                          : 'Open notifications'
                      }
                      title="Notifications"
                    >
                      <Bell className="h-4 w-4" />
                      {notifications.length > 0 && (
                        <span className="fmct-header-notify-count">
                          {notifications.length > 99 ? '99+' : notifications.length}
                        </span>
                      )}
                    </button>
                  )}
                  <p className="fmct-welcome">
                    Welcome{authSession?.name ? `, ${authSession.name}` : ''}
                  </p>
                </div>
              </>
            )}
          </section>

          <div className="fmct-layout">
            <div className="fmct-topnav-row">
              {!isDriverAccount && renderTopnavProjectFilter()}
              <div className="fmct-lot-tabs" role="tablist" aria-label="Fleet sections">
                {visibleNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="tab"
                      aria-selected={activeNav === item.id}
                      className={`fmct-lot-tabs__btn${activeNav === item.id ? ' is-active' : ''}`}
                      onClick={() => setActiveNav(item.id)}
                    >
                      <Icon className="h-3.5 w-3.5" aria-hidden />
                      <span className="fmct-nav-label-full">{item.label}</span>
                      {item.shortLabel ? (
                        <span className="fmct-nav-label-short">{item.shortLabel}</span>
                      ) : null}
                      {item.id === 'vir' && pendingVirInspectionCount > 0 && (
                        <span
                          className="fmct-nav-badge"
                          aria-label={`${pendingVirInspectionCount} vehicle inspection${pendingVirInspectionCount === 1 ? '' : 's'} due`}
                        >
                          {pendingVirInspectionCount > 99 ? '99+' : pendingVirInspectionCount}
                        </span>
                      )}
                      {item.id === 'service-card' && serviceCardNavAlert && (
                        <span
                          className="fmct-nav-badge"
                          title={serviceCardNavAlert.label}
                          aria-label={serviceCardNavAlert.label}
                        >
                          !
                        </span>
                      )}
                      {item.id === 'driving-license' && drivingLicenseNavAlert && (
                        <span
                          className="fmct-nav-badge"
                          title={drivingLicenseNavAlert.label}
                          aria-label={drivingLicenseNavAlert.label}
                        >
                          !
                        </span>
                      )}
                      {item.id === 'notifications' && notifications.length > 0 && (
                        <span
                          className="fmct-nav-badge"
                          aria-label={`${notifications.length} notification${notifications.length === 1 ? '' : 's'}`}
                        >
                          {notifications.length > 99 ? '99+' : notifications.length}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {!isAdminView && renderTopnavVehicleSelect()}
            </div>

            <main className="fmct-main">{renderMain()}</main>
          </div>
        </div>
      </div>

      {drawerVehicle && isAdminView && (
        <div className="fmct-drawer-overlay" onClick={() => setDrawerVehicle(null)} role="presentation">
          <aside
            className="fmct-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Driver details"
          >
            <div className="fmct-drawer-header">
              <div>
                <h2>{getDriverProfileWithRental(drawerVehicle)?.driver || drawerVehicle.driver}</h2>
                <p>Driver profile</p>
              </div>
              <button type="button" className="fmct-drawer-close" onClick={() => setDrawerVehicle(null)} aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="fmct-drawer-section">
              <h4>Driver Information</h4>
              {(() => {
                const profile = getDriverProfileWithRental(drawerVehicle);
                if (!profile) return null;
                const vehicleHistory = profile.vehicleHistory || [];
                return (
                  <div className="fmct-drawer-driver-profile">
                    <div className="fmct-drawer-driver-main">
                      <dl className="fmct-dl">
                        <dt>User name</dt><dd>{profile.driver}</dd>
                        <dt>Project</dt><dd className="fmct-drawer-project">{profile.project}</dd>
                        <dt>Vehicles used</dt><dd>{vehicleHistory.length || 1}</dd>
                        <dt>Phone</dt><dd>{profile.phone || '—'}</dd>
                        <dt>Email</dt><dd>{profile.email || '—'}</dd>
                      </dl>
                      <img
                        src={resolveLicenseImageSrc(profile.driverAvatar)}
                        alt={`${profile.driver} profile`}
                        className="fmct-driver-avatar fmct-driver-avatar--drawer"
                      />
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="fmct-drawer-section">
              <h4>Last used vehicle information</h4>
              <dl className="fmct-dl">
                <dt>Plate</dt><dd>{drawerVehicle.plate}</dd>
                <dt>Model</dt><dd>{drawerVehicle.model}</dd>
                <dt>Owner / Rental company</dt><dd>{drawerVehicle.ownerRentalCompany}</dd>
                <dt>Odometer</dt><dd>{formatKm(drawerVehicle.odometer)}</dd>
                <dt>Status</dt>
                <dd>
                  <span className={`fmct-status fmct-status-${drawerVehicle.displayStatus}`}>
                    {statusLabel(drawerVehicle.displayStatus)}
                  </span>
                </dd>
              </dl>
            </div>

            <div className="fmct-drawer-section">
              <h4>Vehicle used information</h4>
              {(() => {
                const profile = getDriverProfileWithRental(drawerVehicle);
                const vehicleHistory = profile?.vehicleHistory || [];
                if (!vehicleHistory.length) {
                  return <p className="fmct-toolbar-sub">No vehicle history from Car Rental Invoicing.</p>;
                }
                return (
                  <div className="fmct-drawer-vehicle-history">
                    <p className="fmct-toolbar-sub">Assignment history from Car Rental Invoicing by month</p>
                    <div className="fmct-drawer-table-wrap">
                      <table className="fmct-table fmct-driver-history-table">
                        <thead>
                          <tr>
                            <th>Reg number</th>
                            <th>Project</th>
                            <th>Rental company</th>
                            <th>Months used</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vehicleHistory.map((entry) => (
                            <tr
                              key={entry.regNumber}
                              className={
                                normalizeRegNumber(entry.regNumber) === normalizeRegNumber(drawerVehicle.plate)
                                  ? 'fmct-driver-history-row--current'
                                  : ''
                              }
                            >
                              <td>{entry.regNumber}</td>
                              <td>{entry.project || '—'}</td>
                              <td>{entry.rentalCompany || '—'}</td>
                              <td>
                                <div className="fmct-month-chips">
                                  {entry.months.map((m) => (
                                    <span key={m.key} className="fmct-month-chip" title={m.project}>
                                      {m.label}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className={`fmct-drawer-stats ${isAdminView ? '' : 'fmct-drawer-stats--driver'}`}>
              {isAdminView && (
                <div>
                  <span>Total Maintenance</span>
                  <strong>{formatCurrency(getVehicleTotalCost(drawerVehicle.id))}</strong>
                </div>
              )}
              <div>
                <span>Last Service</span>
                <strong>{drawerVehicle.lastServiceDate}</strong>
              </div>
              <div>
                <span>Next Service Due</span>
                <strong>
                  {drawerVehicle.nextServiceDue.isDue
                    ? 'Overdue'
                    : formatKm(drawerVehicle.nextServiceDue.nextKm)}
                </strong>
              </div>
            </div>

            <div className="fmct-drawer-section">
              <h4>Maintenance History</h4>
              <div className="fmct-drawer-table-wrap">
                <table className="fmct-table fmct-table-compact">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Item</th>
                      {isAdminView && <th>Cost</th>}
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drawerRecords.map((r) => (
                      <tr key={r.id}>
                        <td>{r.date}</td>
                        <td>{r.item}</td>
                        {isAdminView && <td>{formatCurrency(r.cost)}</td>}
                        <td>{statusLabel(r.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </aside>
        </div>
      )}

      {drawerLicenseData && isAdminView && (
        <div className="fmct-drawer-overlay" onClick={() => setDrawerLicense(null)} role="presentation">
          <aside
            className="fmct-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Driving license details"
          >
            <div className="fmct-drawer-header">
              <div>
                <h2>{drawerLicenseData.license.fullName}</h2>
                <p>{drawerLicenseData.license.licenseNumber}</p>
              </div>
              <div className="fmct-drawer-header-actions">
                <button
                  type="button"
                  className="fmct-btn fmct-btn-outline fmct-btn-sm"
                  onClick={() => exportDrivingLicensePdf(drawerLicenseData.license, drawerLicenseData.vehicle)}
                >
                  <FileText className="h-4 w-4" /> Download PDF
                </button>
                <button type="button" className="fmct-drawer-close" onClick={() => setDrawerLicense(null)} aria-label="Close">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="fmct-drawer-section">
              <h4>License Details</h4>
              <dl className="fmct-dl">
                <dt>License number</dt><dd>{drawerLicenseData.license.licenseNumber}</dd>
                <dt>Full name</dt><dd>{drawerLicenseData.license.fullName}</dd>
                <dt>Class</dt><dd>{drawerLicenseData.license.licenseClass}</dd>
                <dt>Status</dt>
                <dd>
                  {(() => {
                    const licenseStatus = getLicenseStatus(drawerLicenseData.license.expiryDate);
                    return (
                      <span className={`fmct-status fmct-status-${licenseStatus.key === 'valid' ? 'completed' : licenseStatus.key === 'expiring' ? 'pending' : 'repair'}`}>
                        {licenseStatus.label}
                        {licenseStatus.days >= 0 ? ` (${licenseStatus.days} days)` : ''}
                      </span>
                    );
                  })()}
                </dd>
              </dl>
            </div>

            <div className="fmct-drawer-section">
              <h4>Validity &amp; Authority</h4>
              <dl className="fmct-dl">
                <dt>Issue date</dt><dd>{formatLicenseDisplayDate(drawerLicenseData.license.issueDate)}</dd>
                <dt>Expiry date</dt><dd>{formatLicenseDisplayDate(drawerLicenseData.license.expiryDate)}</dd>
                <dt>Issuing authority</dt><dd>{drawerLicenseData.license.issuingAuthority}</dd>
              </dl>
            </div>

            {drawerLicenseData.vehicle && (
              <div className="fmct-drawer-section">
                <h4>Assigned Vehicle</h4>
                <dl className="fmct-dl">
                  <dt>Driver</dt><dd>{drawerLicenseData.vehicle.driver}</dd>
                  <dt>Plate</dt><dd>{drawerLicenseData.vehicle.plate}</dd>
                  <dt>Model</dt><dd>{drawerLicenseData.vehicle.model}</dd>
                  <dt>Project</dt><dd>{drawerLicenseData.vehicle.project}</dd>
                </dl>
              </div>
            )}

            <div className="fmct-drawer-section">
              <h4>Driving License — Front View</h4>
              <figure className="fmct-dl-image-card fmct-dl-image-card--single fmct-drawer-license-image-wrap">
                <img
                  src={resolveLicenseImageSrc(drawerLicenseData.license.frontImage)}
                  alt={`${drawerLicenseData.license.fullName} driving license front`}
                  className="fmct-dl-image fmct-drawer-license-image"
                />
              </figure>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

export default FleetMaintenanceCostTracking;
